/**
 * buildStructuredAltText - Generate PIPH-64 structured alt text for LLM consumption
 *
 * Transforms workspace feature extraction results into a structured text format
 * suitable for input to an LLM prompt that generates natural language alt text.
 *
 * Per-feature naming: every atom-label lookup goes through an
 * `AtomLabelResolver` built from the feature serialization. The resolver
 * returns either the per-feature label (when given a feature context id) or
 * the default-feature label. No global label map.
 *
 * @package @alchemiesolns/svg-interactive
 * @see PIPH-64
 */

import type { WorkspaceFeatureResult } from '@alchemiesolns/feature-extractor-compat';
import {
  Features,
  serializeFeatures,
  assembleMolecularFormula,
  makeAtomLabelResolver,
  type AtomLabelResolver,
  type SerializationResult,
  type SerializedFeature,
  type SerializedBond,
  type FeatureType,
} from '@alchemiesolns/feature-extractor';
import {
  classifyRelationship,
  classifyPrimaryConnection,
  RECOGNIZED_BRIDGING_GROUPS,
} from '@alchemiesolns/molecule-descriptions';
import { elementDisplayName } from './atomNaming';

/** Primary structural feature types (rings, chains, etc.) */
const PRIMARY_TYPES: Set<FeatureType> = new Set([
  'ring',
  'bridged_bicyclic',
  'chain',
  'atom_cluster',
]);

/**
 * Check if a feature type is a primary structural feature
 * PIPH-112: Exported for reuse in buildAltTextMap for HTML tree descriptions.
 */
export function isPrimaryType(type: FeatureType): boolean {
  return PRIMARY_TYPES.has(type);
}

/**
 * Find the bond connecting two atoms by their indices.
 * PIPH-112: Exported for reuse in buildAltTextMap for HTML tree descriptions.
 */
export function findBondBetweenAtoms(
  atomIndex1: number,
  atomIndex2: number,
  serialized: SerializationResult
): SerializedBond | undefined {
  return serialized.bonds.find(
    b =>
      (b.atomIndices[0] === atomIndex1 && b.atomIndices[1] === atomIndex2) ||
      (b.atomIndices[0] === atomIndex2 && b.atomIndices[1] === atomIndex1)
  );
}

// (removed: buildSerializedGlobalAtomLabels) — replaced by per-feature
// resolution via `makeAtomLabelResolver`. The atom-label single source of
// truth is now `SerializedFeature.atomLabels`, looked up by feature scope.

/**
 * Format bond type label for wedge/dash/squiggle bonds only.
 * Returns empty string for line bonds (single/double/triple with no stereochemistry).
 */
function formatBondTypeLabel(bondType: 'l' | 'w' | 'd' | 's' | undefined): string {
  if (bondType === 'w') return 'wedge bonded';
  if (bondType === 'd') return 'dash bonded';
  if (bondType === 's') return 'squiggle bonded';
  return '';
}

/**
 * Format bond order label for double/triple bonds.
 * Returns empty string for single bonds.
 */
function formatBondOrderLabel(order: number | undefined): string {
  if (order === 2) return 'double bond';
  if (order === 3) return 'triple bond';
  return '';
}

/**
 * Format bond order label for ALL bond orders including single.
 * Used for PIPH-139 unrecognized bridging atom descriptions.
 */
function formatBondOrderLabelFull(order: number | undefined): string {
  if (order === 3) return 'triple bond';
  if (order === 2) return 'double bond';
  return 'single bond';
}

/**
 * PIPH-311: capitalized one-word label for a bond's display type, used in the
 * new feature-block line `"- {Word} bond between {pos1} and {pos2}"`. Returns
 * `null` for plain single line bonds (the default, not surfaced as a line).
 *
 * Precedence: stereo (w/d/s) wins over order because a stereo bond in the
 * current data model is always order 1 — order info is redundant. Higher-order
 * bonds (double/triple) cover the remaining cases.
 */
function bondTypeWordForLine(sb: SerializedBond): string | null {
  if (sb.bondType === 'w') return 'Wedge';
  if (sb.bondType === 'd') return 'Dash';
  if (sb.bondType === 's') return 'Squiggle';
  if (sb.order === 2) return 'Double';
  if (sb.order === 3) return 'Triple';
  return null;
}

/**
 * PIPH-311: a bond qualifies for the new feature-block line when both endpoints
 * are inside the feature AND the bond carries a non-default type or order.
 * Single line bonds are the default and are intentionally not surfaced.
 */
function isNonDefaultIntraFeatureBond(
  sb: SerializedBond,
  featureAtoms: Set<number>,
): boolean {
  const [aIdx, bIdx] = sb.atomIndices;
  if (!featureAtoms.has(aIdx) || !featureAtoms.has(bIdx)) return false;
  return sb.order >= 2 || sb.bondType === 'w' || sb.bondType === 'd' || sb.bondType === 's';
}

/**
 * PIPH-311: collect "{min}:{max}" keys for every C=C / C≡C bond already
 * surfaced as an alkene/alkyne FG line in the output, so the new intra-feature
 * bond loop can skip them and avoid duplicating the FG classification's output.
 *
 * Ring-internal alkenes ARE present in `serialized.features` as alkene
 * features, but the renderer doesn't surface them as FG lines (they're
 * geometry-fixed by the ring, no rotation possible). Skip those too so
 * PIPH-311 correctly emits "Double bond between …" for ring-internal alkenes
 * (card Example 2) — without that skip the carve-out would silently swallow
 * the bond.
 */
function collectAlkeneAlkyneBondPairs(serialized: SerializationResult): Set<string> {
  const ringAtoms = collectRingAtomIndices(serialized);
  const pairs = new Set<string>();
  for (const f of serialized.features) {
    if (f.internalName !== Features.alkene && f.internalName !== Features.alkine) continue;
    if (f.atomIndices.length !== 2) continue;
    const [a, b] = f.atomIndices;
    // Ring-internal alkene FGs aren't rendered as FG lines, so they don't
    // carve out PIPH-311 emissions.
    if (ringAtoms.has(a) && ringAtoms.has(b)) continue;
    const key = a < b ? `${a}:${b}` : `${b}:${a}`;
    pairs.add(key);
  }
  return pairs;
}

/**
 * PIPH-210: Lowercase element-name labels used for fallback position labels
 * when a global atom label is not available (e.g. "nitrogen-2"). Only the
 * common organic elements need to be covered; unknown symbols fall back to
 * the lowercase symbol itself.
 */
const ELEMENT_LABEL_BY_SYMBOL: Record<string, string> = {
  C: 'carbon',
  H: 'hydrogen',
  N: 'nitrogen',
  O: 'oxygen',
  S: 'sulfur',
  P: 'phosphorus',
  F: 'fluorine',
  Cl: 'chlorine',
  Br: 'bromine',
  I: 'iodine',
};

/**
 * PIPH-140: Superscript digits for charge formatting.
 */
const SUPERSCRIPT_DIGITS: Record<number, string> = {
  0: '⁰', 1: '¹', 2: '²', 3: '³', 4: '⁴',
  5: '⁵', 6: '⁶', 7: '⁷', 8: '⁸', 9: '⁹',
};

/**
 * PIPH-140: Format charge as Unicode superscript suffix.
 * Returns empty string for neutral molecules.
 */
function formatChargeSuffix(charge: number | undefined): string {
  if (!charge) return '';
  const sign = charge > 0 ? '⁺' : '⁻';
  const abs = Math.abs(charge);
  if (abs === 1) return sign;
  const digits = String(abs).split('').map(d => SUPERSCRIPT_DIGITS[Number(d)]).join('');
  return digits + sign;
}

/**
 * PIPH-139 / PIPH-212: Recognized bridging functional group internal names now
 * live in `@alchemiesolns/molecule-descriptions` (RECOGNIZED_BRIDGING_GROUPS).
 */
function isRecognizedBridgingGroup(feature: SerializedFeature): boolean {
  return RECOGNIZED_BRIDGING_GROUPS.has(feature.internalName);
}

/**
 * Find the bond connecting a functional group to its parent feature.
 * Looks for a bond between atoms in the functional group and atoms in the parent.
 */
function findFunctionalGroupAttachmentBond(
  fg: SerializedFeature,
  parent: SerializedFeature,
  serialized: SerializationResult
): SerializedBond | undefined {
  const parentAtomSet = new Set(parent.atomIndices);

  // Check bonds from functional group atoms to parent atoms
  for (const fgAtomIdx of fg.atomIndices) {
    for (const parentAtomIdx of parentAtomSet) {
      const bond = findBondBetweenAtoms(fgAtomIdx, parentAtomIdx, serialized);
      if (bond) return bond;
    }
  }

  // Also check connection atoms
  for (const connAtomIdx of fg.connectionAtomIndices) {
    if (parentAtomSet.has(connAtomIdx)) {
      // Find the bond from this connection atom to a functional group atom
      for (const fgAtomIdx of fg.atomIndices) {
        const bond = findBondBetweenAtoms(fgAtomIdx, connAtomIdx, serialized);
        if (bond) return bond;
      }
    }
  }

  return undefined;
}

/**
 * Intermediate structure for building feature text blocks.
 * PIPH-150: Exported for reuse in buildAltTextMap for HTML tree descriptions.
 */
export interface FeatureBlock {
  featureId: string;
  displayName: string;
  lines: string[];
}

/**
 * PIPH-88: Compute bidirectional connection counts between primary features.
 *
 * The serialized `connections` array may only contain one direction of the
 * relationship due to how featureSorter processes siblings. This function
 * computes connections symmetrically by analyzing which features share
 * connecting atoms.
 *
 * Two primary features are connected if:
 * 1. One feature's connectionAtomIndices references atoms in another feature's
 *    atomIndices, OR
 * 2. They have a fusion relationship (fusedTo array)
 */
function computeBidirectionalConnectionCounts(
  primaries: SerializedFeature[],
  _serialized: SerializationResult
): Map<string, number> {
  const connectionCounts = new Map<string, number>();

  // Initialize counts
  for (const feature of primaries) {
    connectionCounts.set(feature.id, 0);
  }

  // Build lookup: atom index -> feature IDs that contain this atom
  const atomToFeatures = new Map<number, Set<string>>();
  for (const feature of primaries) {
    for (const atomIdx of feature.atomIndices) {
      if (!atomToFeatures.has(atomIdx)) {
        atomToFeatures.set(atomIdx, new Set());
      }
      atomToFeatures.get(atomIdx)!.add(feature.id);
    }
  }

  // For each primary feature, check which other primaries its connection atoms touch
  const primaryIds = new Set(primaries.map(f => f.id));
  const countedPairs = new Set<string>(); // To avoid double-counting

  for (const feature of primaries) {
    // Check connection atoms - these are atoms bonded to but outside the feature
    for (const connAtomIdx of feature.connectionAtomIndices) {
      const touchedFeatures = atomToFeatures.get(connAtomIdx);
      if (touchedFeatures) {
        for (const touchedId of touchedFeatures) {
          if (touchedId !== feature.id && primaryIds.has(touchedId)) {
            // Create a canonical pair key to avoid double-counting
            const pairKey = [feature.id, touchedId].sort().join('|');
            if (!countedPairs.has(pairKey)) {
              countedPairs.add(pairKey);
              // Increment count for both sides of the relationship
              connectionCounts.set(feature.id, connectionCounts.get(feature.id)! + 1);
              connectionCounts.set(touchedId, connectionCounts.get(touchedId)! + 1);
            }
          }
        }
      }
    }
  }

  // Also count fused relationships (from fusedTo array)
  for (const feature of primaries) {
    if (feature.fusedTo) {
      for (const fusedId of feature.fusedTo) {
        if (primaryIds.has(fusedId)) {
          const pairKey = [feature.id, fusedId].sort().join('|');
          if (!countedPairs.has(pairKey)) {
            countedPairs.add(pairKey);
            connectionCounts.set(feature.id, connectionCounts.get(feature.id)! + 1);
            connectionCounts.set(fusedId, connectionCounts.get(fusedId)! + 1);
          }
        }
      }
    }
  }

  return connectionCounts;
}

/**
 * Determine the core feature: the primary feature with the most connections
 * to other primaries, using atom count as tiebreaker.
 *
 * PIPH-88: Uses bidirectional connection counting to handle asymmetric
 * sibling relationships in the feature extractor.
 */
function determineCore(
  primaries: SerializedFeature[],
  serialized: SerializationResult
): SerializedFeature | undefined {
  if (primaries.length === 0) return undefined;

  // PIPH-88: Use bidirectional connection counting
  const connectionCounts = computeBidirectionalConnectionCounts(primaries, serialized);

  // Build index map for deterministic fallback (original feature order)
  const indexMap = new Map(primaries.map((f, i) => [f.id, i]));

  // Sort by connection count (desc), then by atom count (desc),
  // then by original feature order (deterministic fallback)
  const sorted = [...primaries].sort((a, b) => {
    const connDiff = connectionCounts.get(b.id)! - connectionCounts.get(a.id)!;
    if (connDiff !== 0) return connDiff;

    const atomDiff = b.atomIndices.length - a.atomIndices.length;
    if (atomDiff !== 0) return atomDiff;

    // Deterministic fallback: first in Feature Extractor order
    return indexMap.get(a.id)! - indexMap.get(b.id)!;
  });

  return sorted[0];
}

/**
 * Group functional groups by their parent feature ID.
 * Returns a map from parent feature ID to array of functional groups,
 * sorted by positionInParent.
 * PIPH-150: Exported for reuse in buildAltTextMap for HTML tree descriptions.
 */
export function groupFunctionalGroupsByParent(
  serialized: SerializationResult
): Map<string, SerializedFeature[]> {
  const groups = new Map<string, SerializedFeature[]>();

  for (const feature of serialized.features) {
    if (feature.type === 'functional_group' && feature.parentFeatureId) {
      const existing = groups.get(feature.parentFeatureId) || [];
      existing.push(feature);
      groups.set(feature.parentFeatureId, existing);
    }
  }

  // Sort each group by positionInParent
  for (const [, children] of groups) {
    children.sort((a, b) => (a.positionInParent || 999) - (b.positionInParent || 999));
  }

  return groups;
}

/**
 * Find a functional group that bridges between two primary features.
 * A bridging group has parentFeatureId = source AND its connection atoms
 * overlap with the target feature's atoms.
 */
function findBridgingGroup(
  source: SerializedFeature,
  target: SerializedFeature,
  serialized: SerializationResult
): SerializedFeature | undefined {
  const sourceAtomSet = new Set(source.atomIndices);
  const targetAtomSet = new Set(target.atomIndices);

  for (const feature of serialized.features) {
    if (feature.type !== 'functional_group') continue;
    if (feature.parentFeatureId !== source.id) continue;

    // PIPH-209: A functional group bridges source and target only if it has a
    // connection atom that lies in `target` but NOT in `source`. When two
    // primaries are fused (e.g. two rings sharing one carbon), the fusion atom
    // is in both atomIndices lists. A heteroatom integrated inside `source`
    // (e.g. an ether O in the 5-ring of a bicyclic acetal) lists that fusion
    // atom in its connectionAtomIndices — without this exclusion it gets
    // misclassified as a bridge between the two rings, which then suppresses
    // it from the source ring's display name.
    const hasConnectionToTargetOnly = feature.connectionAtomIndices.some(
      i => targetAtomSet.has(i) && !sourceAtomSet.has(i)
    );

    if (hasConnectionToTargetOnly) {
      return feature;
    }
  }

  return undefined;
}

/**
 * PIPH-124: Find which atom of a bridging group connects to a target feature.
 * Used to specify "through oxygen of ester" on the non-carbonyl side instead
 * of just "through ester".
 *
 * @param bridgingGroup The functional group bridging two features
 * @param targetFeature The primary feature we're describing the connection FROM
 * @param serialized The full serialization result
 * @returns The element name in lowercase (e.g., "oxygen") or undefined
 */
function findBridgingAtomToFeature(
  bridgingGroup: SerializedFeature,
  targetFeature: SerializedFeature,
  serialized: SerializationResult
): string | undefined {
  const targetAtomSet = new Set(targetFeature.atomIndices);
  const bridgingAtomSet = new Set(bridgingGroup.atomIndices);

  // Find which bridging group atom bonds directly to a target feature atom
  // by walking through all bonds
  for (const bond of serialized.bonds) {
    const [a1, a2] = bond.atomIndices;

    let bridgingAtomIdx: number | undefined;

    // Check if a1 is in bridging group and a2 is in target feature
    if (bridgingAtomSet.has(a1) && targetAtomSet.has(a2)) {
      bridgingAtomIdx = a1;
    }
    // Check if a2 is in bridging group and a1 is in target feature
    else if (bridgingAtomSet.has(a2) && targetAtomSet.has(a1)) {
      bridgingAtomIdx = a2;
    }

    if (bridgingAtomIdx !== undefined) {
      const posInGroup = bridgingGroup.atomIndices.indexOf(bridgingAtomIdx);
      if (posInGroup !== -1) {
        // Extract element name from label like "Oxygen-1" -> "oxygen"
        const label = bridgingGroup.atomLabels[posInGroup];
        const elementMatch = label.match(/^([A-Za-z]+)/);
        return elementMatch ? elementMatch[1].toLowerCase() : undefined;
      }
    }
  }

  return undefined;
}

/**
 * PIPH-208: Symmetric counterpart to findBridgingAtomToFeature.
 * Returns the atom index in `feature.atomIndices` that bonds to any atom in
 * `bridgingGroup.atomIndices`. Used to identify the source-side atom of a
 * connection through a bridging functional group on the non-carbonyl side,
 * where no atom is shared between the feature and the bridging group.
 */
function findFeatureAtomBondedToBridgingGroup(
  feature: SerializedFeature,
  bridgingGroup: SerializedFeature,
  serialized: SerializationResult
): number | undefined {
  const featureAtomSet = new Set(feature.atomIndices);
  const bridgingAtomSet = new Set(bridgingGroup.atomIndices);

  for (const bond of serialized.bonds) {
    const [a1, a2] = bond.atomIndices;
    if (featureAtomSet.has(a1) && bridgingAtomSet.has(a2)) return a1;
    if (featureAtomSet.has(a2) && bridgingAtomSet.has(a1)) return a2;
  }
  return undefined;
}

/**
 * PIPH-100: Representation of a bidirectional connection between two primary features.
 * PIPH-112: Exported for reuse in buildAltTextMap for HTML tree descriptions.
 */
export interface PrimaryConnection {
  /** ID of the target feature this connection points to */
  targetFeatureId: string;
  /** Atom indices on the SOURCE feature that form the connection point */
  sourceAtomIndices: number[];
  /** Atom indices on the TARGET feature that form the connection point */
  targetAtomIndices: number[];
  /** ID of a functional group that bridges these features (if any) */
  bridgingGroupId?: string;
}

/**
 * PIPH-100: Compute bidirectional connections between all primary features.
 * PIPH-112: Exported for reuse in buildAltTextMap for HTML tree descriptions.
 *
 * Unlike the serialized `feature.connections` array which may only contain
 * one direction of the relationship, this function ensures connections are
 * computed symmetrically for both sides.
 *
 * Two primary features are connected if:
 * 1. One feature's connectionAtomIndices references atoms in another feature's
 *    atomIndices (direct connection), OR
 * 2. A functional group bridges them (group's parent is one feature, group's
 *    connectionAtomIndices touch the other feature)
 */
export function computePrimaryConnections(
  serialized: SerializationResult,
  primaries: SerializedFeature[]
): Map<string, PrimaryConnection[]> {
  const connectionMap = new Map<string, PrimaryConnection[]>();
  const primaryIds = new Set(primaries.map(f => f.id));

  // Initialize empty connection arrays
  for (const primary of primaries) {
    connectionMap.set(primary.id, []);
  }

  // Build lookup: atom index -> primary feature IDs that contain this atom
  const atomToPrimary = new Map<number, string>();
  for (const primary of primaries) {
    for (const atomIdx of primary.atomIndices) {
      atomToPrimary.set(atomIdx, primary.id);
    }
  }

  // Track pairs we've already processed (to avoid duplicates)
  const processedPairs = new Set<string>();

  // Method 1: Direct connections via connectionAtomIndices
  for (const source of primaries) {
    for (const connAtomIdx of source.connectionAtomIndices) {
      const targetId = atomToPrimary.get(connAtomIdx);
      if (targetId && targetId !== source.id && primaryIds.has(targetId)) {
        const pairKey = [source.id, targetId].sort().join('|');
        if (!processedPairs.has(pairKey)) {
          processedPairs.add(pairKey);

          const target = primaries.find(p => p.id === targetId)!;

          // Find the atom on source that connects (the one whose bond leads to connAtomIdx)
          // Source's connectionAtomIndices contains atoms OUTSIDE source that source bonds to
          // So connAtomIdx is actually ON the target
          const sourceConnAtoms = source.connectionAtomIndices.filter(
            idx => atomToPrimary.get(idx) === targetId
          );
          const targetConnAtoms = target.connectionAtomIndices.filter(
            idx => atomToPrimary.get(idx) === source.id
          );

          // Check for bridging functional group
          const bridgingGroup = findBridgingGroup(source, target, serialized)
            || findBridgingGroup(target, source, serialized);

          // PIPH-212: Reclassify — when the candidate bridging group has no
          // heavy atoms outside the two primaries (alkene/alkyne between two
          // primaries), it isn't a bridge; it's a direct bond and should leave
          // `bridgingGroupId` unset so the wording becomes "by double bond".
          const effectiveBridgingId = bridgingGroup
            && classifyPrimaryConnection(bridgingGroup, source, target, serialized).kind !== 'direct'
            ? bridgingGroup.id
            : undefined;

          // Add connection from source to target
          connectionMap.get(source.id)!.push({
            targetFeatureId: targetId,
            sourceAtomIndices: sourceConnAtoms,
            targetAtomIndices: targetConnAtoms.length > 0 ? targetConnAtoms : [connAtomIdx],
            bridgingGroupId: effectiveBridgingId,
          });

          // Add reciprocal connection from target to source
          connectionMap.get(targetId)!.push({
            targetFeatureId: source.id,
            sourceAtomIndices: targetConnAtoms.length > 0 ? targetConnAtoms : [connAtomIdx],
            targetAtomIndices: sourceConnAtoms,
            bridgingGroupId: effectiveBridgingId,
          });
        }
      }
    }
  }

  // Method 2: Bridging functional groups (may catch connections not found above)
  for (const fg of serialized.features) {
    if (fg.type !== 'functional_group') continue;
    if (!fg.parentFeatureId || !primaryIds.has(fg.parentFeatureId)) continue;

    // PIPH-209: Skip the fg.parentFeatureId atoms when checking for a touched
    // primary. When two primaries share an atom (e.g. fused rings), that atom
    // is in both, and atomToPrimary collapses to a single id. A heteroatom
    // integrated inside the parent ring (ether O) whose connection includes
    // the fusion atom would otherwise read as bridging across rings.
    const parentAtomSet = new Set(
      serialized.features.find(f => f.id === fg.parentFeatureId)!.atomIndices
    );

    // Find if this functional group touches another primary
    for (const connAtomIdx of fg.connectionAtomIndices) {
      // Skip atoms that are in the parent feature itself — they can't represent
      // a bridge "out" of the parent.
      if (parentAtomSet.has(connAtomIdx)) continue;
      const touchedId = atomToPrimary.get(connAtomIdx);
      if (touchedId && touchedId !== fg.parentFeatureId && primaryIds.has(touchedId)) {
        const pairKey = [fg.parentFeatureId, touchedId].sort().join('|');
        if (!processedPairs.has(pairKey)) {
          processedPairs.add(pairKey);

          const parent = primaries.find(p => p.id === fg.parentFeatureId)!;
          const touched = primaries.find(p => p.id === touchedId)!;

          // PIPH-212: Distinguish a true bridge (ester, ether, …) from a direct
          // bond labeled by the functional group (alkene/alkyne between primaries).
          const info = classifyPrimaryConnection(fg, parent, touched, serialized);

          if (info.kind === 'direct') {
            // The fg's atoms already live inside the two primaries; no extra
            // atoms form a path. Source/target are the fg atoms living in each.
            const touchedAtomSet = new Set(touched.atomIndices);
            const fgInParent = fg.atomIndices.filter(idx => parentAtomSet.has(idx));
            const fgInTouched = fg.atomIndices.filter(idx => touchedAtomSet.has(idx));

            connectionMap.get(fg.parentFeatureId)!.push({
              targetFeatureId: touchedId,
              sourceAtomIndices: fgInParent,
              targetAtomIndices: fgInTouched,
            });
            connectionMap.get(touchedId)!.push({
              targetFeatureId: fg.parentFeatureId,
              sourceAtomIndices: fgInTouched,
              targetAtomIndices: fgInParent,
            });
            continue;
          }

          // For bridging groups, the parent's connection is where the group attaches
          const parentConnAtoms = parent.connectionAtomIndices.filter(
            idx => fg.atomIndices.includes(idx) || fg.connectionAtomIndices.includes(idx)
          );

          // Add connection from parent to target
          connectionMap.get(fg.parentFeatureId)!.push({
            targetFeatureId: touchedId,
            sourceAtomIndices: parentConnAtoms,
            targetAtomIndices: [connAtomIdx],
            bridgingGroupId: fg.id,
          });

          // Add reciprocal connection from target to parent
          connectionMap.get(touchedId)!.push({
            targetFeatureId: fg.parentFeatureId,
            sourceAtomIndices: [connAtomIdx],
            targetAtomIndices: parentConnAtoms,
            bridgingGroupId: fg.id,
          });
        }
      }
    }
  }

  // PIPH-123: Method 3: Use classifyRelationship() to detect bridging functional groups
  // This catches groups like esters where connectionAtomIndices doesn't include all bridged atoms
  // PIPH-145: Handle functional groups that bridge MORE than 2 primaries (e.g., tertiary amides)
  for (const fg of serialized.features) {
    if (fg.type !== 'functional_group') continue;

    const rel = classifyRelationship(fg, serialized);
    if (rel.kind !== 'between' || rel.parentIds.length < 2) continue;

    // PIPH-145: Process ALL pairs of parent IDs, not just first two
    // For a functional group bridging N primaries, create connections between all pairs
    const validParentIds = rel.parentIds.filter(id => primaryIds.has(id));
    if (validParentIds.length < 2) continue;

    // Build a map of which atoms of the FG touch each parent
    const atomsTouchingParent = new Map<string, number[]>();
    for (const parentId of validParentIds) {
      const parent = primaries.find(p => p.id === parentId)!;
      const parentAtomSet = new Set(parent.atomIndices);
      const touchingAtoms: number[] = [];

      for (const fgAtomIdx of fg.atomIndices) {
        const fgAtom = serialized.atoms.find(a => a.index === fgAtomIdx);
        if (!fgAtom) continue;
        for (const bondIndex of fgAtom.bondIndices) {
          const bond = serialized.bonds[bondIndex];
          if (!bond) continue;
          const otherIdx = bond.atomIndices[0] === fgAtomIdx
            ? bond.atomIndices[1]
            : bond.atomIndices[0];
          if (parentAtomSet.has(otherIdx)) {
            touchingAtoms.push(otherIdx);
          }
        }
      }
      atomsTouchingParent.set(parentId, touchingAtoms);
    }

    // Create connections between all pairs of parents
    for (let i = 0; i < validParentIds.length; i++) {
      for (let j = i + 1; j < validParentIds.length; j++) {
        const parentId1 = validParentIds[i];
        const parentId2 = validParentIds[j];

        const pairKey = [parentId1, parentId2].sort().join('|');
        if (processedPairs.has(pairKey)) continue;
        processedPairs.add(pairKey);

        const parent1 = primaries.find(p => p.id === parentId1)!;
        const parent2 = primaries.find(p => p.id === parentId2)!;

        // PIPH-212: Distinguish a true bridge (ester, ether, …) from a direct
        // bond labeled by a functional group (alkene/alkyne between primaries).
        // For "direct" we leave bridgingGroupId undefined and use the fg's own
        // atoms — which sit inside the two primaries — as source/target so the
        // downstream bond lookup finds the inter-primary bond directly.
        const info = classifyPrimaryConnection(fg, parent1, parent2, serialized);

        if (info.kind === 'direct') {
          const parent1AtomSet = new Set(parent1.atomIndices);
          const parent2AtomSet = new Set(parent2.atomIndices);
          const fgInParent1 = fg.atomIndices.filter(idx => parent1AtomSet.has(idx));
          const fgInParent2 = fg.atomIndices.filter(idx => parent2AtomSet.has(idx));

          connectionMap.get(parentId1)!.push({
            targetFeatureId: parentId2,
            sourceAtomIndices: fgInParent1,
            targetAtomIndices: fgInParent2,
          });
          connectionMap.get(parentId2)!.push({
            targetFeatureId: parentId1,
            sourceAtomIndices: fgInParent2,
            targetAtomIndices: fgInParent1,
          });
        } else {
          const atomsTouchingParent1 = atomsTouchingParent.get(parentId1) || [];
          const atomsTouchingParent2 = atomsTouchingParent.get(parentId2) || [];

          connectionMap.get(parentId1)!.push({
            targetFeatureId: parentId2,
            sourceAtomIndices: atomsTouchingParent1,
            targetAtomIndices: atomsTouchingParent2,
            bridgingGroupId: fg.id,
          });
          connectionMap.get(parentId2)!.push({
            targetFeatureId: parentId1,
            sourceAtomIndices: atomsTouchingParent2,
            targetAtomIndices: atomsTouchingParent1,
            bridgingGroupId: fg.id,
          });
        }
      }
    }
  }

  return connectionMap;
}

/**
 * Resolve "at <label>" for an atom in the parent feature, given its 1-indexed
 * local position. The atom is in the parent's scope, so we resolve in the
 * parent's per-feature scope (`parent.id` as `featureContextId`). The
 * renderer never synthesizes labels — it looks up whatever the resolver
 * returns. If the lookup fails, emit `atom-?` as a loud failure marker.
 */
function resolveAtomPositionLabel(
  parent: SerializedFeature,
  positionInParent: number | undefined,
  resolve: AtomLabelResolver,
): string {
  if (positionInParent === undefined) return '';
  const atomIdx = parent.atomIndices[positionInParent - 1];
  return resolveAtomLabel(atomIdx, resolve, parent.id);
}

/**
 * Resolve "at <label>" by atom index. When `featureContextId` is provided
 * and the atom is in that feature, the per-feature label is used; otherwise
 * the resolver returns the atom's default-feature label.
 */
function resolveAtomLabel(
  atomIdx: number | undefined,
  resolve: AtomLabelResolver,
  featureContextId?: string,
): string {
  if (atomIdx === undefined) return 'at atom-?';
  const label = resolve(atomIdx, featureContextId);
  if (!label) return 'at atom-?';
  return `at ${label.replace(/\s*\d*[+-]\s*$/, '').toLowerCase()}`;
}

/**
 * Bare-label variant — returns just the label with no "at " prefix. Returns
 * `atom-?` loud failure marker when the lookup misses.
 */
function labelOrUnknown(
  atomIdx: number | undefined,
  resolve: AtomLabelResolver,
  featureContextId?: string,
): string {
  if (atomIdx === undefined) return 'atom-?';
  const label = resolve(atomIdx, featureContextId);
  if (!label) return 'atom-?';
  return label.replace(/\s*\d*[+-]\s*$/, '').toLowerCase();
}

/**
 * PIPH-91: Representation of an unclaimed atom (atom-type feature not part of
 * any recognized functional group).
 * PIPH-150: Exported for reuse in buildAltTextMap for HTML tree descriptions.
 */
export interface UnclaimedAtom {
  /** The atom feature from serialization (the cluster's representative). */
  feature: SerializedFeature;
  /** Primary feature this atom is attached to (if exactly one) */
  attachedTo: SerializedFeature | null;
  /** If atom bridges multiple primaries, all connected primaries (PIPH-145) */
  bridging: SerializedFeature[] | null;
  /** True if atom is not attached to any primary (standalone ion) */
  standalone: boolean;
  /**
   * PIPH-231: Populated when the atom is bonded to a functional group rather
   * than directly to a primary. The renderer uses this to surface the full
   * attachment path back to the host primary.
   */
  attachedViaFg?: {
    /** The functional group the atom is bonded to. */
    fg: SerializedFeature;
    /** The atom in `fg` that the unclaimed atom bonds to. */
    fgConnectionAtomIndex: number;
    /** The primary that ultimately hosts the FG (read from fg.parentFeatureId). */
    host: SerializedFeature | null;
    /** 1-indexed position of fg's connection atom on `host`, when host exists. */
    hostPosition?: number;
  };
  /** 1-indexed position in the parent feature (if attached) */
  positionInParent?: number;
  /** Bond type connecting to parent (wedge/dash/squiggle/line) */
  bondType?: 'w' | 'd' | 'l' | 's';
  /**
   * PIPH-234: For multi-atom clusters of unrecognized atoms (e.g. an
   * `O-O-H` peroxide chain hanging off a primary), the constituent atom
   * features. Singletons leave this undefined and use `feature` as before.
   */
  clusterFeatures?: SerializedFeature[];
  /**
   * PIPH-234: Additional 1-indexed positions on `attachedTo` that this
   * cluster touches beyond `positionInParent`. Empty / undefined for
   * single-attachment clusters; populated when a cluster bonds to the
   * same primary at multiple atoms (e.g. a cyclic peroxide).
   */
  additionalPositions?: number[];
  /**
   * PIPH-234: True if any heavy atom in the cluster carries a radical.
   * Drives the " radical" suffix on the cluster's rendered formula.
   */
  hasRadical?: boolean;
  /**
   * PIPH-234: Net formal charge summed across all heavy atoms in the
   * cluster. Used by the renderer to append a charge superscript to the
   * cluster's formula and to drive the per-cluster charge bullet.
   */
  netCharge?: number;
}

/**
 * PIPH-91: Format atom display name for unclaimed atoms.
 * PIPH-131: Use feature.name which contains the molecular formula (e.g., "H₂O⁺")
 * for consistency with arrow descriptions.
 */
function formatAtomDisplayName(
  atomFeature: SerializedFeature,
  _serialized: SerializationResult
): string {
  // The feature.name already contains the molecular formula with charge
  // (e.g., "H₂O⁺", "H₃O⁺", "O⁻") from the feature extractor.
  return atomFeature.name;
}

/**
 * PIPH-234: A connectivity-coalesced group of unrecognized atom features.
 *
 * `looseAtomsFilter` produces one atom feature per heavy atom; an O-O-H
 * peroxide hanging off a chain becomes two oxygen features rather than
 * one. The renderer (this module) wants to describe the whole cluster
 * as a single substituent ("HO₂ at carbon-3"), so we group connected
 * atom features here at render time. Edges are atom-to-atom bonds
 * between two atom features only — bonds to primaries or functional
 * groups never create cluster edges (they're the cluster's *exits*,
 * not its internal connectivity).
 */
interface AtomCluster {
  /** Constituent atom features. Length 1 for singletons. */
  features: SerializedFeature[];
  /** Union of every atom in every feature (heavy + bonded H). */
  atomIndices: Set<number>;
  /** Just the heavy atoms — drives formula construction and charge math. */
  heavyAtomIndices: number[];
  /**
   * Outward-facing connection atoms — atoms outside the cluster that
   * any cluster atom is bonded to. Used to find primary attachment.
   */
  connectionAtomIndices: number[];
  /** Sum of formal charges across heavy atoms. */
  netCharge: number;
  /** True if any heavy atom carries a non-zero radical count. */
  hasRadical: boolean;
  /** First atom feature in input order; stands in as the cluster's `feature` field. */
  representative: SerializedFeature;
}

/**
 * PIPH-234: Group atom-type features into clusters by direct atom-to-atom
 * bonds. Two atom features A and B are unioned iff some atom in A's
 * `atomIndices` appears in B's `atomIndices` connection set (i.e. they
 * share a bond). Bonds to primaries / FGs are NOT cluster edges — those
 * are exits.
 *
 * Singleton atom features (no bonds to other atom features) emerge as
 * single-element clusters, so existing PIPH-122 / PIPH-131 H₂O⁺ rendering
 * is unaffected.
 */
export function groupUnclaimedAtomFeaturesIntoClusters(
  atomFeatures: SerializedFeature[],
  serialized: SerializationResult,
): AtomCluster[] {
  // atom index → owning atom feature (only atom-type features).
  const atomToFeature = new Map<number, SerializedFeature>();
  for (const f of atomFeatures) {
    for (const idx of f.atomIndices) atomToFeature.set(idx, f);
  }

  // Union-find over the atomFeatures array indices.
  const parent = atomFeatures.map((_, i) => i);
  const find = (i: number): number => {
    while (parent[i] !== i) {
      parent[i] = parent[parent[i]];
      i = parent[i];
    }
    return i;
  };
  const union = (a: number, b: number): void => {
    const ra = find(a), rb = find(b);
    if (ra !== rb) parent[Math.max(ra, rb)] = Math.min(ra, rb);
  };

  for (let i = 0; i < atomFeatures.length; i++) {
    const f = atomFeatures[i];
    for (const connIdx of f.connectionAtomIndices) {
      const other = atomToFeature.get(connIdx);
      if (other && other !== f) {
        const j = atomFeatures.indexOf(other);
        if (j >= 0) union(i, j);
      }
    }
  }

  // Group by root.
  const groups = new Map<number, SerializedFeature[]>();
  for (let i = 0; i < atomFeatures.length; i++) {
    const root = find(i);
    const list = groups.get(root) ?? [];
    list.push(atomFeatures[i]);
    groups.set(root, list);
  }

  const clusters: AtomCluster[] = [];
  for (const [, features] of groups) {
    const atomIndices = new Set<number>();
    for (const f of features) {
      for (const idx of f.atomIndices) atomIndices.add(idx);
    }

    const heavyAtomIndices: number[] = [];
    let netCharge = 0;
    let hasRadical = false;
    for (const idx of atomIndices) {
      const atom = serialized.atoms.find(a => a.index === idx);
      if (!atom || atom.symbol === 'H') continue;
      heavyAtomIndices.push(idx);
      netCharge += atom.charge ?? 0;
      if ((atom.radicals ?? 0) > 0) hasRadical = true;
    }

    const connectionAtomIndices: number[] = [];
    const seen = new Set<number>();
    for (const f of features) {
      for (const c of f.connectionAtomIndices) {
        if (atomIndices.has(c)) continue; // internal bond — not an exit
        if (seen.has(c)) continue;
        seen.add(c);
        connectionAtomIndices.push(c);
      }
    }

    clusters.push({
      features,
      atomIndices,
      heavyAtomIndices,
      connectionAtomIndices,
      netCharge,
      hasRadical,
      representative: features[0],
    });
  }

  return clusters;
}

/**
 * PIPH-234: Render an unclaimed-cluster's display name.
 *
 * Singletons go through the existing `formatAtomDisplayName` path
 * unchanged — `feature.name` already carries `H₂O⁺` / `O⁻` / `Cl⁻` from
 * the feature extractor, and PIPH-122 / PIPH-131 depend on that string
 * verbatim.
 *
 * Multi-atom clusters get a fresh formula assembled from heavy + bonded
 * H symbols via `assembleMolecularFormula` (Hill ordering — alphabetical
 * with H before O for no-carbon sets, so [O,O,H] → `HO₂`). Net charge
 * is appended as a Unicode superscript via `formatChargeSuffix`, then
 * " radical" is appended when any cluster atom carries a radical.
 */
/**
 * PIPH-314: Identification clauses naming the specific atom(s) in a multi-atom
 * cluster that carry a non-zero charge and/or one or more radicals. Each
 * property-carrying atom contributes one combined clause via
 * `elementDisplayName` (so charge + radical on the same atom collapse to
 * "positively charged oxygen radical"; on different atoms they emit two
 * clauses). Atoms with neither contribute nothing — returns `[]` for a
 * vanilla cluster, so the call site can no-op cleanly.
 *
 * Known ambiguity (accepted by the card): when all heavy atoms in the cluster
 * share an element (e.g. O₂ radical, charge on one O), the clause is just
 * "oxygen radical" with no positional disambiguation. A future cluster-block
 * section card is logged to address this fully.
 */
function buildClusterIdentificationClauses(
  unclaimed: UnclaimedAtom,
  serialized: SerializationResult,
): string[] {
  if (!unclaimed.clusterFeatures || unclaimed.clusterFeatures.length <= 1) {
    return [];
  }
  const clauses: string[] = [];
  const seenAtoms = new Set<number>();
  for (const f of unclaimed.clusterFeatures) {
    for (const idx of f.atomIndices) {
      if (seenAtoms.has(idx)) continue;
      seenAtoms.add(idx);
      const atom = serialized.atoms.find(a => a.index === idx);
      if (!atom) continue;
      if (atom.symbol === 'H') continue;
      const charge = atom.charge ?? 0;
      const radicals = atom.radicals ?? 0;
      if (charge === 0 && radicals === 0) continue;
      clauses.push(elementDisplayName(atom.symbol, { charge, radicals }));
    }
  }
  return clauses;
}

function renderClusterDisplayName(
  unclaimed: UnclaimedAtom,
  serialized: SerializationResult,
): string {
  if (!unclaimed.clusterFeatures || unclaimed.clusterFeatures.length <= 1) {
    return formatAtomDisplayName(unclaimed.feature, serialized);
  }
  // Note: serialization folds explicit hydrogen atoms into each heavy atom's
  // `hydrogenCount`. The atom-feature `atomIndices` list contains heavy atoms
  // only (and any H atom indices that survived `looseAtomsFilter` will not be
  // in `serialized.atoms` either). Build the formula symbol multiset by
  // taking each heavy atom's symbol once plus `hydrogenCount` H symbols.
  const symbols: string[] = [];
  const seenAtoms = new Set<number>();
  for (const f of unclaimed.clusterFeatures) {
    for (const idx of f.atomIndices) {
      if (seenAtoms.has(idx)) continue;
      seenAtoms.add(idx);
      const atom = serialized.atoms.find(a => a.index === idx);
      if (!atom) continue;
      if (atom.symbol === 'H') {
        symbols.push('H');
        continue;
      }
      symbols.push(atom.symbol);
      for (let i = 0; i < (atom.hydrogenCount ?? 0); i++) symbols.push('H');
    }
  }
  const formula = assembleMolecularFormula(symbols);
  const chargeSuffix = formatChargeSuffix(unclaimed.netCharge ?? 0);
  const radicalSuffix = unclaimed.hasRadical ? ' radical' : '';
  return formula + chargeSuffix + radicalSuffix;
}

/**
 * PIPH-231: Strip "of {parent}" suffix and trailing letter disambiguation
 * (e.g. "ether-A") from a feature display name. Mirrors `stripDisplaySuffixes`
 * in molecule-descriptions; copied locally to avoid a cross-package barrel
 * change for one helper.
 */
function stripFeatureNameSuffixes(name: string): string {
  const ofIdx = name.indexOf(' of ');
  const trimmed = ofIdx > 0 ? name.substring(0, ofIdx) : name;
  return trimmed.replace(/-[A-Z]+$/, '');
}

/**
 * PIPH-231: Build the attachment-path sentence for an unrecognized atom that
 * is bonded to a functional group rather than directly to a primary.
 *
 * Shape:
 *   "connected to {connElement} of {fgName}"
 *   "connected to {connElement} of {fgName} at {hostPositionLabel} of {hostName}"  (when host known)
 *
 * Example:
 *   "connected to oxygen of ether at carbon-1 of 2-carbon chain"
 */
function buildFgAttachmentPathSentence(
  uncl: UnclaimedAtom,
  serialized: SerializationResult,
  resolve: AtomLabelResolver,
): string {
  if (!uncl.attachedViaFg) return '';
  const { fg, fgConnectionAtomIndex, host, hostPosition } = uncl.attachedViaFg;

  const connAtom = serialized.atoms.find(a => a.index === fgConnectionAtomIndex);
  const connElement = connAtom
    ? (ELEMENT_LABEL_BY_SYMBOL[connAtom.symbol] ?? connAtom.symbol.toLowerCase())
    : 'atom';
  const fgName = stripFeatureNameSuffixes(fg.name);

  if (host && hostPosition !== undefined) {
    // Resolve the host-side atom in the host's per-feature scope so the
    // position label matches the host's own numbering.
    const hostAtomIdx = host.atomIndices[hostPosition - 1];
    const rawLabel = hostAtomIdx !== undefined
      ? resolve(hostAtomIdx, host.id)
      : undefined;
    const positionLabel = rawLabel
      ? rawLabel.replace(/\s*\d*[+-]\s*$/, '').toLowerCase()
      : `position ${hostPosition}`;
    const hostName = stripFeatureNameSuffixes(host.name);
    return `connected to ${connElement} of ${fgName} at ${positionLabel} of ${hostName}`;
  }

  return `connected to ${connElement} of ${fgName}`;
}

/**
 * PIPH-91: Classify unclaimed atoms by their relationship to primary features.
 *
 * Unclaimed atoms are "atom" type features created by looseAtomsFilter for
 * atoms not part of any recognized ring, chain, or functional group.
 *
 * Classification:
 * - Attached: Bonds to exactly one primary feature → listed under that primary
 * - Bridging: Bonds to two primary features → connection line format
 * - Standalone: No bonds to primaries → simple molecule format
 *
 * PIPH-150: Exported for reuse in buildAltTextMap for HTML tree descriptions.
 *
 * PIPH-234: The unit of classification is now an *atom cluster* (a
 * connectivity-coalesced group of unrecognized atom features). Single-atom
 * features are singleton clusters and behave exactly as before; multi-atom
 * clusters (e.g. an `O-O-H` peroxide chain) collapse into one classification
 * entry so the renderer can produce a single "with HO₂ at carbon-3" line.
 */
export function classifyUnclaimedAtoms(
  atomFeatures: SerializedFeature[],
  primaries: SerializedFeature[],
  serialized: SerializationResult
): UnclaimedAtom[] {
  const result: UnclaimedAtom[] = [];

  // Build lookup: atom index → primary feature
  const atomToPrimary = new Map<number, SerializedFeature>();
  for (const primary of primaries) {
    for (const atomIdx of primary.atomIndices) {
      atomToPrimary.set(atomIdx, primary);
    }
  }

  // PIPH-231: lookup atom index → functional group (mirrors atomToPrimary).
  // Used to detect atoms bonded to a FG rather than a primary.
  const atomToFg = new Map<number, SerializedFeature>();
  for (const f of serialized.features) {
    if (f.type === 'functional_group') {
      for (const atomIdx of f.atomIndices) {
        atomToFg.set(atomIdx, f);
      }
    }
  }

  // PIPH-234: coalesce connected atom features into clusters before
  // classifying. Singletons survive as one-feature clusters and the rest of
  // the function treats them identically to today's per-feature loop.
  const clusters = groupUnclaimedAtomFeaturesIntoClusters(atomFeatures, serialized);

  for (const cluster of clusters) {
    const isMultiAtom = cluster.features.length > 1;
    // Common UnclaimedAtom fields populated for every classification branch.
    const baseEntry: Pick<UnclaimedAtom, 'feature' | 'clusterFeatures' | 'hasRadical' | 'netCharge'> = {
      feature: cluster.representative,
      clusterFeatures: isMultiAtom ? cluster.features : undefined,
      hasRadical: cluster.hasRadical || undefined,
      netCharge: cluster.netCharge !== 0 ? cluster.netCharge : undefined,
    };

    // Find which primaries this cluster is connected to, and which atoms on
    // the FIRST connected primary are touched (for multi-position phrasing).
    const connectedPrimaries = new Set<SerializedFeature>();
    let firstPrimary: SerializedFeature | undefined;
    const positionsOnFirstPrimary: number[] = [];
    let attachmentBondType: 'w' | 'd' | 'l' | 's' | undefined;
    let positionInParent: number | undefined;

    for (const connAtomIdx of cluster.connectionAtomIndices) {
      const primary = atomToPrimary.get(connAtomIdx);
      if (!primary) continue;
      connectedPrimaries.add(primary);
      if (!firstPrimary) firstPrimary = primary;
      if (primary === firstPrimary) {
        const posIdx = primary.atomIndices.indexOf(connAtomIdx);
        if (posIdx >= 0) {
          const pos = posIdx + 1; // 1-indexed
          if (!positionsOnFirstPrimary.includes(pos)) {
            positionsOnFirstPrimary.push(pos);
          }
          if (positionInParent === undefined) {
            positionInParent = pos;
            // Find the bond on the cluster atom that bonds to this primary
            // atom; bond type carries through the existing wedge/dash phrasing.
            for (const heavyIdx of cluster.heavyAtomIndices) {
              const bond = findBondBetweenAtoms(heavyIdx, connAtomIdx, serialized);
              if (bond) {
                attachmentBondType = bond.bondType;
                break;
              }
            }
          }
        }
      }
    }

    const primariesArray = Array.from(connectedPrimaries);
    positionsOnFirstPrimary.sort((a, b) => a - b);
    const additionalPositions = positionsOnFirstPrimary
      .filter(p => p !== positionInParent);

    if (primariesArray.length === 0) {
      // PIPH-231: Before falling through to standalone, check whether this
      // cluster touches a functional group (e.g. TBDMS on the oxygen of an
      // ether). The serializer already records this via parentFeatureId
      // pointing at the FG; we surface the full attachment path here for
      // the renderer.
      let fgMatch: { fg: SerializedFeature; fgConnectionAtomIndex: number } | undefined;
      for (const connAtomIdx of cluster.connectionAtomIndices) {
        const fg = atomToFg.get(connAtomIdx);
        if (fg) {
          fgMatch = { fg, fgConnectionAtomIndex: connAtomIdx };
          break;
        }
      }

      if (fgMatch) {
        const host = fgMatch.fg.parentFeatureId
          ? primaries.find(p => p.id === fgMatch!.fg.parentFeatureId) ?? null
          : null;
        let hostPosition: number | undefined;
        if (host) {
          const fgConnToHost = fgMatch.fg.connectionAtomIndices.find(idx =>
            host.atomIndices.includes(idx)
          );
          if (fgConnToHost !== undefined) {
            const posIdx = host.atomIndices.indexOf(fgConnToHost);
            if (posIdx >= 0) hostPosition = posIdx + 1;
          }
        }
        result.push({
          ...baseEntry,
          attachedTo: null,
          bridging: null,
          standalone: false,
          attachedViaFg: {
            fg: fgMatch.fg,
            fgConnectionAtomIndex: fgMatch.fgConnectionAtomIndex,
            host,
            hostPosition,
          },
        });
        continue;
      }

      // Standalone cluster (not attached to any primary or FG)
      result.push({
        ...baseEntry,
        attachedTo: null,
        bridging: null,
        standalone: true,
      });
    } else if (primariesArray.length === 1) {
      // Attached to exactly one primary, possibly at multiple atoms.
      result.push({
        ...baseEntry,
        attachedTo: primariesArray[0],
        bridging: null,
        standalone: false,
        positionInParent,
        additionalPositions: additionalPositions.length > 0 ? additionalPositions : undefined,
        bondType: attachmentBondType,
      });
    } else {
      // PIPH-145: Bridging multiple primaries - store ALL connected primaries
      result.push({
        ...baseEntry,
        attachedTo: null,
        bridging: primariesArray,
        standalone: false,
        positionInParent,
        bondType: attachmentBondType,
      });
    }
  }

  return result;
}

/**
 * PIPH-91: Group unclaimed atoms by their parent primary feature.
 * PIPH-150: Exported for reuse in buildAltTextMap for HTML tree descriptions.
 */
export function groupUnclaimedAtomsByParent(
  unclaimedAtoms: UnclaimedAtom[]
): Map<string, UnclaimedAtom[]> {
  const groups = new Map<string, UnclaimedAtom[]>();

  for (const atom of unclaimedAtoms) {
    if (atom.attachedTo) {
      const parentId = atom.attachedTo.id;
      const existing = groups.get(parentId) || [];
      existing.push(atom);
      groups.set(parentId, existing);
    }
  }

  // Sort by position in parent
  for (const [, atoms] of groups) {
    atoms.sort((a, b) => (a.positionInParent || 999) - (b.positionInParent || 999));
  }

  return groups;
}

/**
 * PIPH-106: Groups that should be treated as Pattern D when most atoms are
 * inside a ring, even if the carbonyl oxygen (=O) is geometrically outside.
 * These are cyclic carbonyl groups (lactones, lactams) where the C-O-C or
 * C-N-C portion is inside the ring but the C=O is outside.
 */
const SPECIAL_INTEGRATED_GROUPS = new Set([
  'carbonyl.ester',    // Lactones - ester integrated in ring
  'carbonyl.amide',    // Lactams - amide integrated in ring
  'carbonyl.amide.1',
  'carbonyl.amide.2',
  'carbonyl.amide.3',
]);

/**
 * PIPH-125: Check if a functional group is Pattern D (integrated INTO a ring).
 * Pattern D groups should be folded into the feature header, not listed separately.
 *
 * PIPH-106: Enhanced to handle lactones/lactams where the carbonyl oxygen is
 * geometrically outside the ring but the group is chemically integrated.
 */
function isPatternDGroup(
  fg: SerializedFeature,
  result: SerializationResult
): boolean {
  const rel = classifyRelationship(fg, result);

  // Standard Pattern D: classifyRelationship returns "in"
  if (rel.kind === 'in') {
    return true;
  }

  // PIPH-106: Special handling for lactones/lactams
  // These groups have carbonyl oxygen outside the ring but should still be Pattern D
  if (fg.internalName && SPECIAL_INTEGRATED_GROUPS.has(fg.internalName)) {
    // Check if this is actually a cyclic version (lactone/lactam)
    // by verifying the parent is a ring and most atoms are inside
    const parent = result.features.find(f => f.id === fg.parentFeatureId);
    if (parent && parent.type === 'ring') {
      const parentAtomSet = new Set(parent.atomIndices);
      const insideCount = fg.atomIndices.filter(idx => parentAtomSet.has(idx)).length;
      const totalCount = fg.atomIndices.length;

      // If at least half the atoms are inside the ring, treat as Pattern D
      // For ester: C and O (ester) inside, O (carbonyl) outside -> 2/3 inside
      if (insideCount >= totalCount / 2) {
        return true;
      }
    }
  }

  return false;
}

/**
 * PIPH-106: Helper to strip "of {parent}" suffix from a group name.
 */
function stripOfSuffix(name: string): string {
  const ofIndex = name.indexOf(' of ');
  return ofIndex > 0 ? name.substring(0, ofIndex) : name;
}

/**
 * PIPH-232: Join clauses with English-language conjunction.
 *   ["x"]            → "x"
 *   ["x", "y"]       → "x and y"
 *   ["x", "y", "z"]  → "x, y, and z"
 * Matches the existing two-item phrasing used by the Pattern D path so
 * regression-style assertions like `/oxygen-\d+ and oxygen-\d+/` keep
 * passing, while extending to the Oxford-comma form for 3+ contributors.
 */
function joinWithAnd(items: string[]): string {
  if (items.length === 0) return '';
  if (items.length === 1) return items[0];
  if (items.length === 2) return `${items[0]} and ${items[1]}`;
  return `${items.slice(0, -1).join(', ')}, and ${items[items.length - 1]}`;
}

/**
 * PIPH-125: Get the appropriate name for a Pattern D group in the feature header.
 * - Single-atom groups (secondary amine, ether, sulfide): use element name
 * - Multi-atom groups (ester, amide, lactone, lactam): use functional group name
 * - For all cases, strip "of {parent}" suffix if present
 *
 * PIPH-106: Include position information in fact block:
 * - Single-atom: "nitrogen at position 3"
 * - Multi-atom: "ester at carbon-1"
 *
 * Position is rendered from the per-feature label resolver — never from
 * `fg.positionInParent` directly. The parent feature is passed in so we can
 * map local position back to the global atom index, then resolve in the
 * parent's per-feature scope.
 */
function getPatternDName(
  fg: SerializedFeature,
  parent: SerializedFeature,
  resolve: AtomLabelResolver,
  serialized: SerializationResult,
): string {
  // PIPH-310: Map base types to element symbols; the new prose helper
  // (`elementDisplayName`) handles symbol→lowercase-name conversion and
  // applies charge/radical adjectives.
  const singleAtomToSymbol: Record<string, string> = {
    'amine': 'N',
    'ether': 'O',
    'sulfide': 'S',
  };

  // Resolve the position label inside the parent's per-feature scope. For
  // Pattern D groups the relevant atom in the parent is the one bonded to the
  // group (single-atom groups *are* the atom; multi-atom groups have one of
  // their atoms shared with the parent ring).
  const positionLabel = resolveAtomPositionLabel(
    parent,
    fg.positionInParent,
    resolve,
  );

  // PIPH-106: Defensive check for missing internalName
  if (!fg.internalName) {
    // Fallback to multi-atom handling using display name
    const name = stripOfSuffix(fg.name);
    return positionLabel ? `${name} ${positionLabel}` : name;
  }

  const baseType = fg.internalName.split('.')[0];

  if (singleAtomToSymbol[baseType]) {
    const symbol = singleAtomToSymbol[baseType];
    // PIPH-310: Surface charge/radical on the underlying atom in the prose.
    const atomIdx = fg.atomIndices[0];
    const atom = atomIdx !== undefined
      ? serialized.atoms.find(a => a.index === atomIdx)
      : undefined;
    const elementName = elementDisplayName(symbol, {
      charge: atom?.charge,
      radicals: atom?.radicals,
    });
    return collapseRedundantElementPrefix(elementName, positionLabel);
  }

  // Multi-atom: "ester at carbon-1"
  const name = stripOfSuffix(fg.name);
  return positionLabel ? `${name} ${positionLabel}` : name;
}

/**
 * Single-atom Pattern D contributors (ether oxygens, amine nitrogens,
 * unrecognized backbone heteroatoms like boron) ARE the element they
 * describe. If the global label already begins with that element name
 * (e.g. "oxygen-3" for an ether oxygen), prefixing with the element name
 * again would yield "oxygen at oxygen-3" — redundant. Collapse to the bare
 * label in that case. If no positional information is available, fall
 * back to the lone element name.
 *
 * PIPH-232: Extracted from getPatternDName so the unrecognized-backbone
 * folding pass can reuse the same heuristic and the two paths can't drift.
 */
function collapseRedundantElementPrefix(
  elementName: string,
  positionLabel: string,
): string {
  if (!positionLabel) return elementName;
  const bareLabel = positionLabel.replace(/^at /, '');
  if (bareLabel.startsWith(elementName)) return bareLabel;
  return `${elementName} ${positionLabel}`;
}

/**
 * PIPH-232: Find ring backbone heteroatoms that no functional-group
 * extractor claimed.
 *
 * Recognized heteroatoms-in-rings (an amine N, an ether O, a sulfide S)
 * are folded into the ring's display name via the Pattern D path. But
 * heteroatoms that fail every extractor's pattern — most commonly boron,
 * silicon, or oxygens flanked by a non-carbon — never become functional
 * groups, are owned by the ring itself, and so vanish entirely from the
 * generated description.
 *
 * This helper surfaces them so they can participate in the same "with
 * <element> at <position>" clause used for Pattern D contributors.
 *
 * Returns global atom indices, sorted by the numeric suffix on the global
 * label (so "oxygen-1" precedes "boron-2" precedes "oxygen-3" regardless
 * of where they sit in `feature.atomIndices`, which follows graph-traversal
 * order and would otherwise scramble fused rings).
 */
function findUnrecognizedBackboneHeteroatoms(
  feature: SerializedFeature,
  serialized: SerializationResult,
  resolve: AtomLabelResolver,
): number[] {
  // Atoms claimed by ANY functional group (Pattern D or otherwise). A
  // heteroatom owned by an off-ring FG should be described by that FG, not
  // by the ring it happens to overlap with. This is the broader exclusion
  // the existing `atomsClaimedByMultiAtomPatternD` set in `buildFeatureBlock`
  // captures for multi-atom groups; we union in single-atom FG atoms too so
  // recognized ether oxygens etc. continue to be the FG's responsibility.
  const claimedByFgs = new Set<number>();
  for (const f of serialized.features) {
    if (f.type === 'functional_group') {
      for (const idx of f.atomIndices) claimedByFgs.add(idx);
    }
  }

  // Atoms shared with another ring (fusion vertices). When a heteroatom sits
  // at the fusion point of two rings (e.g. the bridgehead nitrogen in
  // indolizine), folding it into both rings' headers would double-print.
  const sharedWithOtherRing = new Set<number>();
  for (const other of serialized.features) {
    if (other === feature || other.type !== 'ring') continue;
    for (const idx of other.atomIndices) {
      if (feature.atomIndices.includes(idx)) sharedWithOtherRing.add(idx);
    }
  }

  const matches: number[] = [];
  for (const idx of feature.atomIndices) {
    const atom = serialized.atoms[idx];
    if (!atom) continue;
    if (atom.symbol === 'C' || atom.symbol === 'H') continue;
    if (claimedByFgs.has(idx)) continue;
    if (sharedWithOtherRing.has(idx)) continue;
    matches.push(idx);
  }

  // Sort by the numeric suffix of the per-feature label so the rendered
  // clause reads in label order ("oxygen-1, boron-2, and oxygen-3") rather
  // than graph-traversal order. We're inside `feature`'s scope, so the
  // numeric suffix comes from `feature.atomLabels` (via the resolver).
  // Atoms with no numeric suffix (single-instance elements) sort first; ties
  // broken by the global atom index for stable output.
  const suffixOf = (i: number): number => {
    const label = resolve(i, feature.id);
    if (!label) return Number.MAX_SAFE_INTEGER;
    const m = label.match(/-(\d+)/);
    return m ? Number(m[1]) : 0;
  };
  matches.sort((a, b) => {
    const diff = suffixOf(a) - suffixOf(b);
    return diff !== 0 ? diff : a - b;
  });
  return matches;
}

/**
 * PIPH-232: Render an unrecognized-backbone heteroatom as a Pattern-D-style
 * contributor (e.g. `boron-2`, or `boron at position 2` when no global
 * label is available). The returned string slots into the same
 * "<ring> with X, Y, and Z" clause used for true Pattern D groups.
 */
function describeUnrecognizedBackboneAtom(
  atomIdx: number,
  serialized: SerializationResult,
  resolve: AtomLabelResolver,
  featureContextId: string,
): string {
  const atom = serialized.atoms[atomIdx];
  // PIPH-310: Apply charge/radical adjectives through the shared utility so a
  // ring-backbone heteroatom that's charged or radical surfaces both here.
  const elementName = atom
    ? elementDisplayName(atom.symbol, {
        charge: atom.charge,
        radicals: atom.radicals,
      })
    : 'atom';
  const rawLabel = resolve(atomIdx, featureContextId);
  const positionLabel = rawLabel
    ? `at ${rawLabel.replace(/\s*\d*[+-]\s*$/, '').toLowerCase()}`
    : '';
  return collapseRedundantElementPrefix(elementName, positionLabel);
}

/**
 * Build a text block for a single primary feature.
 * Includes functional groups, unclaimed atoms, fusion relationships, and connections.
 *
 * PIPH-100: Uses pre-computed bidirectional connections instead of relying on
 * the potentially incomplete feature.connections array.
 *
 * PIPH-91: Also includes unclaimed atoms attached to this feature.
 *
 * PIPH-125: Pattern D groups (integrated into ring) are folded into the header.
 *
 * Per-feature labels: every in-feature lookup uses `feature.id` as the
 * resolver context, so labels always come from `feature.atomLabels`.
 *
 * PIPH-150: Exported for reuse in buildAltTextMap for HTML tree descriptions.
 */
/**
 * PIPH-269: collect every atom index that belongs to a ring or bridged-bicyclic
 * primary feature. Used to suppress E/Z labels on ring-internal alkenes — those
 * double bonds have geometry fixed by the ring, so there's no rotation possible
 * and no E/Z isomerism to describe.
 */
function collectRingAtomIndices(serialized: SerializationResult): Set<number> {
  const inRing = new Set<number>();
  for (const f of serialized.features) {
    if (f.type === 'ring' || f.type === 'bridged_bicyclic') {
      for (const idx of f.atomIndices) inRing.add(idx);
    }
  }
  return inRing;
}

export function buildFeatureBlock(
  feature: SerializedFeature,
  functionalGroups: SerializedFeature[],
  attachedAtoms: UnclaimedAtom[],
  bridgingAtoms: UnclaimedAtom[],
  serialized: SerializationResult,
  primaries: SerializedFeature[],
  connectionMap: Map<string, PrimaryConnection[]>,
  resolve: AtomLabelResolver,
): FeatureBlock {
  const lines: string[] = [];

  // PIPH-100: Get bidirectional connections for this feature
  const connections = connectionMap.get(feature.id) || [];

  // PIPH-100: Identify functional groups that are used as bridges.
  // These should NOT be listed as substituents (they appear only in connection lines).
  const bridgingGroupIds = new Set(
    connections
      .filter(c => c.bridgingGroupId)
      .map(c => c.bridgingGroupId!)
  );

  // 0. (PIPH-315) Bridged-bicyclic structural sentences. The IUPAC numbering
  //    applied to feature.atomIndices upstream (PIPH-118) means labelOrUnknown
  //    returns "carbon-1"…"carbon-N" in the order:
  //      bh1, path1 intermediates, bh2, path2 intermediates, bridge
  //    so the path lines read sequentially around the ring. Regression of
  //    e5ac8e27: rendering code was lost in the molecule-descriptions →
  //    svg-interactive refactor; the serialization layer still populates
  //    bridgeheadAtomIndices, mainRingPaths, and bridgeAtomIndices.
  if (
    feature.type === 'bridged_bicyclic' &&
    feature.bridgeheadAtomIndices?.length === 2 &&
    feature.mainRingPaths?.length === 2
  ) {
    const labelOf = (idx: number) => labelOrUnknown(idx, resolve, feature.id);
    const [bh1, bh2] = feature.bridgeheadAtomIndices;

    lines.push(`- Bridgeheads at ${labelOf(bh1)} and ${labelOf(bh2)}`);

    const path1 = feature.mainRingPaths[0];
    lines.push(`- First path: ${path1.map(labelOf).join(' to ')}`);

    // Both raw mainRingPaths start at bridgeheads[0] (see computeBicyclicPaths
    // in serializeFeatures.ts); the spec reads bh2 → … → bh1 for the second
    // path so it follows IUPAC sequential numbering around the ring.
    const path2 = [...feature.mainRingPaths[1]].reverse();
    lines.push(`- Second path: ${path2.map(labelOf).join(' to ')}`);

    const bridge = feature.bridgeAtomIndices ?? [];
    if (bridge.length > 0) {
      lines.push(`- Bridge: ${bridge.map(labelOf).join(' to ')}`);
    }
  }

  // 1. Functional groups first (ordered by position in parent)
  // PIPH-100: Filter out bridging groups from substituent list
  // PIPH-212: Also filter out inter-primary groups (alkene/alkyne between two
  // primaries) — they're described in the connection sentence ("by double bond"),
  // not as a "- alkene at carbon-X" substituent line.
  const displayedGroups = functionalGroups.filter(fg => {
    if (bridgingGroupIds.has(fg.id)) return false;
    if (classifyRelationship(fg, serialized).kind === 'between') return false;
    return true;
  });

  // PIPH-125: Separate Pattern D groups (integrated into ring) from others
  const allPatternDGroups = displayedGroups.filter(fg => isPatternDGroup(fg, serialized));
  const nonPatternDGroups = displayedGroups.filter(fg => !isPatternDGroup(fg, serialized));

  // PIPH-106: Suppress single-atom Pattern D groups (ether, amine, sulfide) whose atoms
  // are claimed by multi-atom Pattern D groups (lactone, lactam). This prevents the
  // ether oxygen in a lactone from appearing separately as "with oxygen".
  const singleAtomTypes = new Set(['amine', 'ether', 'sulfide']);
  const atomsClaimedByMultiAtomPatternD = new Set<number>();

  for (const fg of allPatternDGroups) {
    const baseType = fg.internalName?.split('.')[0] ?? '';
    if (!singleAtomTypes.has(baseType)) {
      // This is a multi-atom Pattern D group - claim its atoms
      for (const atomIdx of fg.atomIndices) {
        atomsClaimedByMultiAtomPatternD.add(atomIdx);
      }
    }
  }

  // Filter out single-atom Pattern D groups whose atoms overlap with multi-atom groups
  const patternDGroups = allPatternDGroups.filter(fg => {
    const baseType = fg.internalName?.split('.')[0] ?? '';
    if (!singleAtomTypes.has(baseType)) {
      // Multi-atom groups keep themselves
      return true;
    }
    // Single-atom groups: check if their atoms overlap with multi-atom groups
    const hasOverlap = fg.atomIndices.some(idx => atomsClaimedByMultiAtomPatternD.has(idx));
    return !hasOverlap;
  });

  // PIPH-125: Build feature name with Pattern D groups folded in.
  // PIPH-232: Also fold in ring-backbone heteroatoms that no FG extractor
  // claimed (boron, silicon, oxygens flanked by non-carbon, etc.), so the
  // ring header reflects every non-carbon atom in the backbone — matching
  // the Pattern D treatment recognized heteroatoms already get.
  let displayName = feature.name;
  const patternDClauses = patternDGroups.map(fg =>
    getPatternDName(fg, feature, resolve, serialized),
  );
  const unrecognizedHeteroatoms = feature.type === 'ring'
    ? findUnrecognizedBackboneHeteroatoms(feature, serialized, resolve)
    : [];
  const heteroatomClauses = unrecognizedHeteroatoms.map(idx =>
    describeUnrecognizedBackboneAtom(idx, serialized, resolve, feature.id),
  );
  const allClauses = [...patternDClauses, ...heteroatomClauses];
  if (allClauses.length > 0) {
    displayName = `${feature.name} with ${joinWithAnd(allClauses)}`;
  }

  for (const fg of nonPatternDGroups) {
    // Find the bond connecting this functional group to the parent
    const attachmentBond = findFunctionalGroupAttachmentBond(fg, feature, serialized);
    const bondTypeLabel = formatBondTypeLabel(attachmentBond?.bondType);
    const positionLabel = resolveAtomPositionLabel(feature, fg.positionInParent, resolve);

    // PIPH-125: Use simplified name for groups that are integrated into this feature
    // E.g., "alkene of 5-membered ring" → "alkene" when listed under the ring
    let groupName = fg.name;
    const rel = classifyRelationship(fg, serialized);
    if (rel.kind === 'in' && fg.parentFeatureId === feature.id) {
      // Remove "of {parent}" suffix for integrated groups
      const ofIndex = groupName.indexOf(' of ');
      if (ofIndex > 0) {
        groupName = groupName.substring(0, ofIndex);
      }
    }

    // Format: "group bond-type at carbon-X" or "group at carbon-X"
    let line: string;
    if (bondTypeLabel && positionLabel) {
      line = `- ${groupName} ${bondTypeLabel} ${positionLabel}`;
    } else if (positionLabel) {
      line = `- ${groupName} ${positionLabel}`;
    } else if (bondTypeLabel) {
      line = `- ${groupName} ${bondTypeLabel}`;
    } else {
      line = `- ${groupName}`;
    }
    lines.push(line);
  }

  // 1.5 (PIPH-91): Unclaimed atoms / clusters attached to this feature.
  // PIPH-234: an attached entry may be a singleton atom feature (existing
  // path — H₂O⁺, Cl⁻, etc.) OR a multi-atom cluster (e.g. peroxide-on-chain
  // → "HO₂ at carbon-3"). The two cases share the same rendering shape;
  // the difference is in the display name and the multi-position phrasing.
  for (const atom of attachedAtoms) {
    const isMultiAtomCluster = !!atom.clusterFeatures && atom.clusterFeatures.length > 1;
    const displayName = renderClusterDisplayName(atom, serialized);
    const bondTypeLabel = formatBondTypeLabel(atom.bondType);

    // Multi-position phrasing (PIPH-234): when a cluster touches the same
    // primary at multiple atoms (e.g. cyclic peroxide on a chain at
    // carbon-3 and carbon-5), join all positions via the existing
    // joinWithAnd helper from PIPH-232.
    const allPositions = atom.positionInParent !== undefined
      ? [atom.positionInParent, ...(atom.additionalPositions ?? [])]
      : [];
    let positionLabel: string;
    if (allPositions.length === 0) {
      positionLabel = '';
    } else if (allPositions.length === 1) {
      positionLabel = resolveAtomPositionLabel(feature, allPositions[0], resolve);
    } else {
      const labels = allPositions
        .map(p => resolveAtomPositionLabel(feature, p, resolve))
        .map(s => s.replace(/^at /, ''))
        .filter(Boolean);
      positionLabel = labels.length > 0 ? `at ${joinWithAnd(labels)}` : '';
    }

    // PIPH-233: singleton-cluster radicals render with the bare symbol per
    // the original ticket's expected output. Multi-atom clusters get the
    // " radical" suffix already baked into their display name by
    // renderClusterDisplayName, so they fall through to the standard
    // rendering path.
    const innerAtomIdx = atom.feature.atomIndices[0];
    const innerAtom = innerAtomIdx !== undefined
      ? serialized.atoms.find(a => a.index === innerAtomIdx)
      : undefined;
    const singletonRadical = !isMultiAtomCluster && (innerAtom?.radicals ?? 0) > 0;

    // PIPH-261: For singletons, look up the bond order to the parent so the
    // line can read "Oxygen-A double bonded at carbon-1" instead of bare
    // "Oxygen-A at carbon-1". Mirrors the bridging-atom pattern at PIPH-139.
    // Single-position only; multi-position singletons fall through unchanged.
    // The shared `formatBondOrderLabelFull` returns "single bond" / "double
    // bond" / "triple bond" (noun phrase, suits the bridging "through X to Y"
    // grammar). Here the line is in adjectival position ("X bonded at Y") so
    // we convert to the participle form locally.
    let bondOrderLabel = '';
    if (
      !isMultiAtomCluster &&
      innerAtom !== undefined &&
      atom.positionInParent !== undefined &&
      (atom.additionalPositions?.length ?? 0) === 0
    ) {
      const parentAtomIdx = feature.atomIndices[atom.positionInParent - 1];
      if (parentAtomIdx !== undefined) {
        const bond = findBondBetweenAtoms(parentAtomIdx, innerAtom.index, serialized);
        bondOrderLabel = formatBondOrderLabelFull(bond?.order).replace(/ bond$/, ' bonded');
      }
    }

    // PIPH-261: Fold a non-carbon (or ±2+ carbon) singleton charge into the
    // atom's line as "with ±N charge". Carbon ±1 keeps its own
    // Carbocation/Carbanion line below.
    let chargeSuffix = '';
    if (
      !isMultiAtomCluster &&
      innerAtom &&
      innerAtom.charge !== 0 &&
      !(innerAtom.symbol === 'C' && (innerAtom.charge === 1 || innerAtom.charge === -1))
    ) {
      const sign = innerAtom.charge > 0 ? '+' : '-';
      const mag = Math.abs(innerAtom.charge);
      chargeSuffix = ` with ${sign}${mag} charge`;
    }

    // Format like functional groups: "O⁻ wedge bonded at carbon-5" or "O⁻ at carbon-5"
    let line: string;
    if (singletonRadical && innerAtom) {
      // PIPH-310: surface the full element name + charge/radical adjectives
      // here. Previously emitted "- O radical at carbon-2"; now emits
      // "- oxygen radical at carbon-2" (or "- positively charged oxygen
      // radical ..." when charged as well).
      const atomLabel = elementDisplayName(innerAtom.symbol, {
        charge: innerAtom.charge,
        radicals: innerAtom.radicals,
      });
      line = positionLabel
        ? `- ${atomLabel} ${positionLabel}`
        : `- ${atomLabel}`;
    } else if (!isMultiAtomCluster) {
      // PIPH-261: singleton attached atoms include bond order + charge on one line.
      const parts: string[] = [`- ${displayName}`];
      if (bondOrderLabel) parts.push(bondOrderLabel);
      if (bondTypeLabel) parts.push(bondTypeLabel);
      if (positionLabel) parts.push(positionLabel);
      line = parts.join(' ') + chargeSuffix;
    } else if (bondTypeLabel && positionLabel) {
      line = `- ${displayName} ${bondTypeLabel} ${positionLabel}`;
    } else if (positionLabel) {
      line = `- ${displayName} ${positionLabel}`;
    } else if (bondTypeLabel) {
      line = `- ${displayName} ${bondTypeLabel}`;
    } else {
      line = `- ${displayName}`;
    }

    // PIPH-314: identification clause(s) naming the specific atom-with-property
    // in a multi-atom cluster. Replaces the old per-cluster "+N charge on HO₂"
    // bullet (PIPH-210) since the inline clause now says WHICH atom carries
    // the property — strictly more informative. Singleton branches already
    // surface charge/radical inline (PIPH-261, PIPH-310) so they no-op here.
    if (isMultiAtomCluster) {
      const clauses = buildClusterIdentificationClauses(atom, serialized);
      if (clauses.length > 0) {
        line += `, ${clauses.join(', ')}`;
      }
    }
    lines.push(line);

    // PIPH-210 / PIPH-261: standalone Carbocation/Carbanion bullet for
    // singleton carbon ±1 — different rhetorical pattern from the inline
    // charge surfacing used elsewhere. Multi-atom cluster charges are now
    // covered by the PIPH-314 identification clauses above; the old
    // "+N charge on HO₂" bullet has been removed in favor of that.
    if (
      !isMultiAtomCluster &&
      innerAtom &&
      innerAtom.symbol === 'C' &&
      (innerAtom.charge === 1 || innerAtom.charge === -1)
    ) {
      if (innerAtom.charge === 1) {
        lines.push(`- Carbocation${positionLabel ? ' ' + positionLabel : ''}`);
      } else {
        lines.push(`- Carbanion${positionLabel ? ' ' + positionLabel : ''}`);
      }
    }
  }

  // 1.75 (PIPH-210): Formal charges on atoms that are part of this feature.
  //   Carbon  +1 → "Carbocation at <label>"
  //   Carbon  -1 → "Carbanion at <label>"
  //   anything else (carbon ±2+, N±, O±, …) → "+N charge on <label>" / "-N charge on <label>"
  for (const atomIdx of feature.atomIndices) {
    const atom = serialized.atoms.find(a => a.index === atomIdx);
    if (!atom || atom.charge === 0) continue;

    const positionLabel = labelOrUnknown(atomIdx, resolve, feature.id);

    if (atom.symbol === 'C' && atom.charge === 1) {
      lines.push(`- Carbocation at ${positionLabel}`);
    } else if (atom.symbol === 'C' && atom.charge === -1) {
      lines.push(`- Carbanion at ${positionLabel}`);
    } else {
      const sign = atom.charge > 0 ? '+' : '-';
      const mag = Math.abs(atom.charge);
      lines.push(`- ${sign}${mag} charge on ${positionLabel}`);
    }
  }

  // 1.8 (PIPH-233): Radicals on atoms that are part of this feature. Same
  // wording for all radical counts (count is preserved in the data model for
  // future use; the card's expected output uses the bare "radical" form).
  for (const atomIdx of feature.atomIndices) {
    const atom = serialized.atoms.find(a => a.index === atomIdx);
    if (!atom || !atom.radicals) continue;
    const positionLabel = labelOrUnknown(atomIdx, resolve, feature.id);
    lines.push(`- radical at ${positionLabel}`);
  }

  // 1.85 (PIPH-311): non-default intra-feature bond lines. Surface wedge,
  // dash, double, triple, and squiggle bonds wholly inside this feature so
  // the reader knows about stereo / order changes at the feature-block level
  // (atom-description level already covers them per §6.2). Skip bonds that
  // the FG classifier already surfaces as alkene/alkyne lines so we don't
  // duplicate that information. Ring-internal alkenes are filtered out of
  // alkene FG status upstream by inRingFilter, so they correctly get this
  // line — see card Example 2.
  {
    const featureAtomSet = new Set(feature.atomIndices);
    const fgCoveredBondPairs = collectAlkeneAlkyneBondPairs(serialized);
    for (const sb of serialized.bonds) {
      if (!isNonDefaultIntraFeatureBond(sb, featureAtomSet)) continue;
      const [a, b] = sb.atomIndices;
      const pairKey = a < b ? `${a}:${b}` : `${b}:${a}`;
      if (fgCoveredBondPairs.has(pairKey)) continue;
      const word = bondTypeWordForLine(sb);
      if (!word) continue;
      const aLabel = labelOrUnknown(a, resolve, feature.id);
      const bLabel = labelOrUnknown(b, resolve, feature.id);
      lines.push(`- ${word} bond between ${aLabel} and ${bLabel}`);
    }
  }

  // 1.9 (PIPH-269): R/S on stereocenters in this feature.
  for (const atomIdx of feature.atomIndices) {
    const atom = serialized.atoms.find(a => a.index === atomIdx);
    if (!atom?.chirality) continue;
    const positionLabel = labelOrUnknown(atomIdx, resolve, feature.id);
    const tag = atom.chirality === 'r' ? 'R' : 'S';
    lines.push(`- ${tag} configuration at ${positionLabel}`);
  }

  // 1.95 (PIPH-269): E/Z on alkenes wholly inside this feature, but skip
  // ring-internal alkenes — they have fixed geometry imposed by the ring,
  // so there's no rotation around the double bond and no E/Z to report.
  const ringAtoms = collectRingAtomIndices(serialized);
  const featureAtomSet = new Set(feature.atomIndices);
  for (const sb of serialized.bonds) {
    if (sb.order !== 2 || !sb.isomerConfig) continue;
    const [aIdx, bIdx] = sb.atomIndices;
    if (!featureAtomSet.has(aIdx) || !featureAtomSet.has(bIdx)) continue;
    if (ringAtoms.has(aIdx) && ringAtoms.has(bIdx)) continue;
    const tag = sb.isomerConfig === 'e' ? 'E' : 'Z';
    lines.push(`- ${tag} configuration on alkene`);
  }

  // 2. Fusion relationships
  // PIPH-114: Include shared atom positions in fusion lines
  if (feature.fusedTo && feature.fusedSharedAtoms) {
    for (let i = 0; i < feature.fusedTo.length; i++) {
      const fusedId = feature.fusedTo[i];
      const sharedAtoms = feature.fusedSharedAtoms[i] || [];
      const fusedFeature = serialized.features.find(f => f.id === fusedId);
      if (fusedFeature) {
        // Render shared atom positions via the global label map (single source
        // of truth). Order shared atoms by their position within this feature
        // so the description reads in atom order.
        const orderedSharedAtoms = [...sharedAtoms]
          .filter(atomIdx => feature.atomIndices.indexOf(atomIdx) >= 0)
          .sort((a, b) => feature.atomIndices.indexOf(a) - feature.atomIndices.indexOf(b));

        const labels = orderedSharedAtoms.map(atomIdx => resolveAtomLabel(atomIdx, resolve, feature.id));

        let positionStr = '';
        if (labels.length === 1) {
          positionStr = ` ${labels[0]}`;
        } else if (labels.length >= 2) {
          positionStr = ` ${labels[0]} and ${labels[1].replace(/^at /, '')}`;
        }

        lines.push(`- Fused to ${fusedFeature.name}${positionStr}`);
      }
    }
  } else if (feature.fusedTo) {
    // Fallback if fusedSharedAtoms not available
    for (const fusedId of feature.fusedTo) {
      const fusedFeature = serialized.features.find(f => f.id === fusedId);
      if (fusedFeature) {
        lines.push(`- Fused to ${fusedFeature.name}`);
      }
    }
  }

  // 3. Connections to external primary features
  // PIPH-100: Use computed bidirectional connections
  // PIPH-114: Build set of feature IDs that this feature is fused to (for connection suppression)
  // PIPH-145: Group connections by bridging group to combine targets with "and"
  const fusedFeatureIds = new Set(feature.fusedTo || []);

  // First pass: collect connection info and group by bridging group
  // PIPH-148: sourcePositionLabel is now a string (e.g., "carbon-5") instead of number
  // to support global atom labels when available
  interface ConnectionInfo {
    targetFeature: SerializedFeature;
    bridgingGroup: SerializedFeature | undefined;
    sourcePositionLabel: string | undefined;
    bondTypeLabel: string;
    bondOrderLabel: string;
    isNonCarbonylSide: boolean;
    throughPhrase: string;
    // PIPH-269: E/Z of the connecting double bond, for between-feature alkenes
    // (the per-feature E/Z pass can't reach these — neither feature owns both
    // C=C carbons). Only set for order-2 bonds that carry an isomerConfig.
    isomerConfig?: 'e' | 'z';
  }

  const connectionInfos: ConnectionInfo[] = [];

  for (const conn of connections) {
    const targetFeature = primaries.find(p => p.id === conn.targetFeatureId);
    if (!targetFeature) continue;

    // PIPH-114: Skip connection lines for features that are fused (fusion line replaces it)
    if (fusedFeatureIds.has(conn.targetFeatureId)) continue;

    // Find bridging functional group if any
    const bridgingGroup = conn.bridgingGroupId
      ? serialized.features.find(f => f.id === conn.bridgingGroupId)
      : undefined;

    // PIPH-101: Determine position on this feature by finding which atom bonds to
    // the TARGET feature. We directly check for bonds between this feature's atoms
    // and the target feature's atoms (not conn.targetAtomIndices, which may contain
    // atoms within this feature and cause us to find internal bonds).
    // PIPH-148: Use global atom labels when available for consistent naming
    let sourcePositionLabel: string | undefined;

    // Find which atom in this feature bonds to an atom in the target feature
    // and resolve its label via the global label map (single source of truth).
    for (const featureAtomIdx of feature.atomIndices) {
      let bonded = false;
      for (const targetAtomIdx of targetFeature.atomIndices) {
        if (findBondBetweenAtoms(featureAtomIdx, targetAtomIdx, serialized)) {
          bonded = true;
          break;
        }
      }
      if (bonded) {
        sourcePositionLabel = labelOrUnknown(featureAtomIdx, resolve, feature.id);
        break;
      }
    }

    // If bridging group, find where the bridging group attaches to this feature
    if (!sourcePositionLabel && bridgingGroup && bridgingGroup.positionInParent) {
      if (bridgingGroup.parentFeatureId === feature.id) {
        const posIdx = bridgingGroup.positionInParent - 1; // Convert to 0-indexed
        const featureAtomIdx = feature.atomIndices[posIdx];
        sourcePositionLabel = labelOrUnknown(featureAtomIdx, resolve, feature.id);
      }
    }

    // PIPH-208: Non-carbonyl side of a bridging functional group.
    // The carbonyl-side fallback above only handles the parent feature of the
    // bridging group (where an atom is shared between feature and group). On
    // the non-carbonyl side no atom is shared, so we walk bonds to find which
    // atom in this feature bonds to one of the bridging group's atoms, then
    // look up its global label.
    if (
      !sourcePositionLabel &&
      bridgingGroup &&
      bridgingGroup.parentFeatureId !== feature.id
    ) {
      const featureAtomIdx = findFeatureAtomBondedToBridgingGroup(
        feature,
        bridgingGroup,
        serialized
      );
      if (featureAtomIdx !== undefined) {
        sourcePositionLabel = labelOrUnknown(featureAtomIdx, resolve, feature.id);
      }
    }

    // Find the bond connecting the features (for bond type info)
    let connectionBond: SerializedBond | undefined;
    for (const sourceAtomIdx of conn.sourceAtomIndices) {
      for (const targetAtomIdx of conn.targetAtomIndices) {
        connectionBond = findBondBetweenAtoms(sourceAtomIdx, targetAtomIdx, serialized);
        if (connectionBond) break;
      }
      if (connectionBond) break;
    }
    // If no direct bond found, check feature atoms to target atoms
    if (!connectionBond) {
      for (const featureAtomIdx of feature.atomIndices) {
        for (const targetAtomIdx of conn.targetAtomIndices) {
          connectionBond = findBondBetweenAtoms(featureAtomIdx, targetAtomIdx, serialized);
          if (connectionBond) break;
        }
        if (connectionBond) break;
      }
    }

    const bondTypeLabel = formatBondTypeLabel(connectionBond?.bondType);
    const bondOrderLabel = formatBondOrderLabel(connectionBond?.order);
    // PIPH-269: capture E/Z only for an actual double bond (alkynes have a
    // bond-order label but no isomerConfig).
    const isomerConfig = connectionBond?.order === 2 ? connectionBond?.isomerConfig : undefined;

    // Determine throughPhrase for bridging groups
    let throughPhrase = '';
    let isNonCarbonylSide = false;
    if (bridgingGroup) {
      // PIPH-124: Determine if we're on the non-carbonyl side of the bridging group.
      isNonCarbonylSide = bridgingGroup.parentFeatureId !== feature.id;
      throughPhrase = `through ${bridgingGroup.name}`;
      // PIPH-252: anhydride is symmetric (carbonyl on both sides of the bridging
      // oxygen), so the carbonyl/non-carbonyl distinction does not apply — both
      // sides should read "through anhydride" with no atom specification.
      const isSymmetricBridging = bridgingGroup.internalName === Features.anhydride;
      if (isNonCarbonylSide && !isSymmetricBridging) {
        const connectingAtom = findBridgingAtomToFeature(bridgingGroup, feature, serialized);
        if (connectingAtom) {
          throughPhrase = `through ${connectingAtom} of ${bridgingGroup.name}`;
        }
      }
    }

    connectionInfos.push({
      targetFeature,
      bridgingGroup,
      sourcePositionLabel,
      bondTypeLabel,
      bondOrderLabel,
      isNonCarbonylSide,
      throughPhrase,
      isomerConfig,
    });
  }

  // PIPH-147: Deduplicate connections to the same target.
  // When multiple connections exist to the same target (e.g., with and without bridging group),
  // prefer the one WITH bridging group info (more specific).
  const deduplicatedInfos: ConnectionInfo[] = [];
  const byTarget = new Map<string, ConnectionInfo[]>();
  for (const info of connectionInfos) {
    const targetId = info.targetFeature.id;
    const list = byTarget.get(targetId) || [];
    list.push(info);
    byTarget.set(targetId, list);
  }
  for (const list of byTarget.values()) {
    if (list.length === 1) {
      deduplicatedInfos.push(list[0]);
    } else {
      // Prefer connection WITH bridging group (more specific)
      const withBridge = list.find(c => c.bridgingGroup);
      deduplicatedInfos.push(withBridge ?? list[0]);
    }
  }

  // PIPH-145: Group connections by bridging group ID + position + throughPhrase
  // to combine targets that share the same bridging configuration
  // PIPH-148: Use sourcePositionLabel (string) instead of sourcePosition (number)
  const groupedConnections = new Map<string, ConnectionInfo[]>();
  for (const info of deduplicatedInfos) {
    const key = `${info.bridgingGroup?.id ?? 'none'}|${info.sourcePositionLabel ?? 'none'}|${info.throughPhrase}|${info.bondTypeLabel}|${info.bondOrderLabel}`;
    const group = groupedConnections.get(key) || [];
    group.push(info);
    groupedConnections.set(key, group);
  }

  // Generate connection lines, combining targets for same bridge
  for (const group of groupedConnections.values()) {
    if (group.length === 0) continue;

    const first = group[0];
    // PIPH-148: sourcePositionLabel is already the full label (e.g., "carbon-5")
    const positionPart = first.sourcePositionLabel ? `at ${first.sourcePositionLabel}` : '';

    // PIPH-145: Combine target names with "and"
    const targetNames = group.map(c => c.targetFeature.name).join(' and ');

    // Build the connection line based on what information we have
    if (first.bridgingGroup) {
      if (first.bondTypeLabel) {
        lines.push(`- Connected ${positionPart}${positionPart ? ', ' : ''}${first.bondTypeLabel}, ${first.throughPhrase} to ${targetNames}`);
      } else {
        lines.push(`- Connected ${positionPart}${positionPart ? ' ' : ''}${first.throughPhrase} to ${targetNames}`);
      }
    } else if (first.bondOrderLabel) {
      // PIPH-212: Direct connection labeled by bond order (alkene/alkyne between
      // primaries). Phrase as "by double bond" / "by triple bond" — the two
      // primaries share the bond, no extra atoms form a path.
      lines.push(`- Connected ${positionPart}${positionPart ? ' ' : ''}by ${first.bondOrderLabel} to ${targetNames}`);
      // PIPH-269: surface E/Z for a BETWEEN-feature alkene — the per-feature E/Z
      // pass (section 1.95) can't, since neither feature owns both C=C carbons.
      // No ring-skip needed: a between-feature alkene is never ring-internal (both
      // carbons in one ring would be a single ring feature, not a between
      // connection). Mirrors the within-feature line "- E configuration on alkene".
      if (first.isomerConfig) {
        lines.push(`- ${first.isomerConfig === 'e' ? 'E' : 'Z'} configuration on alkene`);
      }
    } else if (first.bondTypeLabel) {
      lines.push(`- Connected ${positionPart}${positionPart ? ', ' : ''}${first.bondTypeLabel}, to ${targetNames}`);
    } else {
      lines.push(`- Connected ${positionPart}${positionPart ? ' ' : ''}to ${targetNames}`);
    }
  }

  // 3.5 (PIPH-91): Bridging atoms connecting this feature to other primaries
  // PIPH-139: Include bond order in the description
  // PIPH-145: Handle multi-branch bridging atoms (e.g., tertiary amide connecting to multiple features)
  for (const bridgingAtom of bridgingAtoms) {
    if (!bridgingAtom.bridging) continue;

    // PIPH-145: Get ALL other primaries (filter out current feature)
    const otherPrimaries = bridgingAtom.bridging.filter(p => p.id !== feature.id);
    if (otherPrimaries.length === 0) continue;

    // PIPH-234: bridging entry may be a cluster — use the cluster-aware helper
    // so a multi-atom bridge renders as e.g. "HO₂" not "Oxygen-A".
    // PIPH-310: For singleton atom-type bridges, surface the prose form with
    // charge/radical adjectives (e.g. "positively charged nitrogen") instead
    // of the title-case feature name ("Nitrogen"). Multi-atom clusters keep
    // the formula-style rollup from `renderClusterDisplayName`.
    const isSingletonAtomBridge =
      (!bridgingAtom.clusterFeatures || bridgingAtom.clusterFeatures.length <= 1) &&
      bridgingAtom.feature.type === 'atom' &&
      bridgingAtom.feature.atomIndices.length === 1;
    let atomDisplayName: string;
    if (isSingletonAtomBridge) {
      const a = serialized.atoms.find(
        x => x.index === bridgingAtom.feature.atomIndices[0],
      );
      atomDisplayName = a
        ? elementDisplayName(a.symbol, {
            charge: a.charge,
            radicals: a.radicals,
          })
        : renderClusterDisplayName(bridgingAtom, serialized);
    } else {
      atomDisplayName = renderClusterDisplayName(bridgingAtom, serialized);
    }
    const bondTypeLabel = formatBondTypeLabel(bridgingAtom.bondType);

    // Find the parent-side atom in `feature` that bonds to the bridging
    // atom (or any cluster atom), and capture its bond order. The parent-
    // side atom is the source of the connection sentence and must use its
    // own global atom label so descriptions agree with the HTML tree.
    const bridgingAtomIndices = bridgingAtom.clusterFeatures
      ? bridgingAtom.clusterFeatures.flatMap(f => f.atomIndices)
      : bridgingAtom.feature.atomIndices;
    let bondOrder: number | undefined;
    let parentSideAtomIdx: number | undefined;
    outerBridgeBondScan: for (const bridgeIdx of bridgingAtomIndices) {
      for (const featureAtomIdx of feature.atomIndices) {
        const bond = findBondBetweenAtoms(bridgeIdx, featureAtomIdx, serialized);
        if (bond) {
          bondOrder = bond.order;
          parentSideAtomIdx = featureAtomIdx;
          break outerBridgeBondScan;
        }
      }
    }
    const bondOrderLabel = formatBondOrderLabelFull(bondOrder);

    const parentSideLabel =
      parentSideAtomIdx !== undefined ? resolve(parentSideAtomIdx, feature.id) : undefined;
    const positionPart = parentSideLabel ? `at ${parentSideLabel.toLowerCase()}` : '';

    // PIPH-139: Format connection through bridging atom
    // Recognized groups use their name; unrecognized use "through {bond order} to {formula}"
    let throughPhrase: string;
    if (isRecognizedBridgingGroup(bridgingAtom.feature)) {
      // Recognized bridging groups use their display name
      throughPhrase = `through ${atomDisplayName}`;
    } else {
      // Unrecognized bridging atoms use bond order + formula
      throughPhrase = `through ${bondOrderLabel} to ${atomDisplayName}`;
    }

    // PIPH-145: List all connected features (join with "and" for multiple)
    const targetNames = otherPrimaries.map(p => p.name).join(' and ');

    if (bondTypeLabel && positionPart) {
      lines.push(`- Connected ${positionPart}, ${bondTypeLabel}, ${throughPhrase} to ${targetNames}`);
    } else if (positionPart) {
      lines.push(`- Connected ${positionPart} ${throughPhrase} to ${targetNames}`);
    } else {
      lines.push(`- Connected ${throughPhrase} to ${targetNames}`);
    }
  }

  // 3.6 (PIPH-323): Non-owning side of a shared-carbon bridge. PIPH-316 made
  // shared-carbon ester/amide classify as "at" (a substituent on the carbonyl
  // chain), which dropped the connection line on the OTHER feature (the ester's
  // O-alkyl / the amide's N-alkyl side). Restore it on that non-owning side,
  // rephrased per the card: "Connected at carbon-X to {atom} of {group} on
  // {owning feature}." Owning side is untouched (keeps "ester at carbon-X").
  for (const fg of serialized.features) {
    if (fg.type !== 'functional_group') continue;
    const name = fg.internalName;
    if (name !== 'carbonyl.ester' && name !== 'carbonyl.amide.2' && name !== 'carbonyl.amide.3') {
      continue;
    }
    // Owning side keeps its substituent line; only the non-owning side needs this.
    if (!fg.parentFeatureId || fg.parentFeatureId === feature.id) continue;
    if (classifyRelationship(fg, serialized).kind !== 'at') continue;

    // This feature must bond to the FG's hetero atom (O/N) to be the non-owning side.
    const featureAtomIdx = findFeatureAtomBondedToBridgingGroup(feature, fg, serialized);
    if (featureAtomIdx === undefined) continue;

    const owning = primaries.find(p => p.id === fg.parentFeatureId);
    if (!owning) continue;

    const connectingAtom = findBridgingAtomToFeature(fg, feature, serialized);
    if (!connectingAtom) continue;

    const carbonLabel = resolve(featureAtomIdx, feature.id)?.toLowerCase();
    if (!carbonLabel) continue;

    lines.push(
      `- Connected at ${carbonLabel} to ${connectingAtom} of ${fg.name} on ${owning.name}`,
    );
  }

  // 4. If no items, add placeholder
  if (lines.length === 0) {
    lines.push('- (no additional functional groups)');
  }

  return {
    featureId: feature.id,
    displayName, // PIPH-125: Use display name with Pattern D groups folded in
    lines,
  };
}

/**
 * Format all feature blocks into the final PIPH-64 output string.
 * PIPH-91: Also includes standalone atoms (ions not attached to any primary).
 * PIPH-231: Unrecognized atoms (truly standalone or FG-attached) each get their
 * own header block at the end, with an attachment-path bullet when applicable.
 */
function formatOutput(
  core: SerializedFeature | undefined,
  blocks: FeatureBlock[],
  unrecognizedAtoms: UnclaimedAtom[] = [],
  serialized?: SerializationResult,
  resolve?: AtomLabelResolver,
): string {
  if (!core || blocks.length === 0) {
    // PIPH-91: If we only have standalone atoms, list them.
    // PIPH-234: Each entry may be a singleton atom feature OR a multi-atom
    // cluster — renderClusterDisplayName handles both.
    if (unrecognizedAtoms.length > 0 && serialized) {
      return unrecognizedAtoms
        .map(a => renderClusterDisplayName(a, serialized))
        .join('\n');
    }
    return 'Empty structure';
  }

  const output: string[] = [];

  // Core line
  output.push(`Core: ${core.name}`);
  output.push('');

  // Reorder blocks: core first, then others
  const coreBlock = blocks.find(b => b.featureId === core.id);
  const otherBlocks = blocks.filter(b => b.featureId !== core.id);
  const orderedBlocks = coreBlock ? [coreBlock, ...otherBlocks] : otherBlocks;

  for (const block of orderedBlocks) {
    output.push(`${block.displayName}:`);
    for (const line of block.lines) {
      output.push(line);
    }
    output.push('');
  }

  // PIPH-231: Unrecognized atoms each get their own header block. For
  // FG-attached atoms the bullet describes the attachment path; for true
  // standalone atoms the bullet is just the display name (preserves PIPH-91
  // behavior for free counterions like Cl⁻, Na⁺).
  // PIPH-234: Each entry may be a multi-atom cluster (e.g. a free O-O-H);
  // renderClusterDisplayName produces the rolled-up formula for those.
  if (unrecognizedAtoms.length > 0 && serialized) {
    const effectiveResolve = resolve ?? makeAtomLabelResolver(serialized);
    for (const atom of unrecognizedAtoms) {
      const displayName = renderClusterDisplayName(atom, serialized);
      output.push(`${displayName}:`);
      if (atom.attachedViaFg) {
        output.push(`- ${buildFgAttachmentPathSentence(atom, serialized, effectiveResolve)}`);
      } else {
        output.push(`- ${displayName}`);
      }
      output.push('');
    }
  }

  return output.join('\n').trim();
}

/**
 * Generate PIPH-64 structured alt text from workspace features.
 *
 * The output format is designed for input to an LLM prompt that generates
 * natural language alt text descriptions of molecular structures.
 *
 * Per-feature naming: an `AtomLabelResolver` is built internally from the
 * serialization. Feature blocks pass their own `feature.id` as the
 * `featureContextId` so all in-feature labels come from
 * `SerializedFeature.atomLabels`.
 *
 * @param featureResult - Output from extractWorkspaceFeatures()
 * @param allAtomSymbols - All atom symbols for formula (includes implicit H)
 * @param netCharge - Net formal charge for the molecule
 * @returns Structured text string per PIPH-64 format
 *
 * @example
 * ```
 * Core: 6-membered ring
 *
 * 6-membered ring:
 * - Ketone at carbon-1
 * - Alcohol at carbon-5
 * - Fused to 5-membered ring
 *
 * 5-membered ring:
 * - (no additional functional groups)
 * ```
 */
export function buildStructuredAltText(
  featureResult: WorkspaceFeatureResult,
  allAtomSymbols?: string[],  // PIPH-140: All atom symbols for formula (includes implicit H)
  netCharge?: number,         // PIPH-140: Net formal charge for the molecule
): string {
  // Step 1: Serialize features to get relationship data
  const serialized = serializeFeatures(
    featureResult.features.map(wf => wf.feature),
    featureResult.graph
  );

  // Build the per-feature atom-label resolver once. Every block downstream
  // uses it with its own `feature.id` to scope label lookups.
  const resolve = makeAtomLabelResolver(serialized);

  // Step 2: Identify primary features
  const primaries = serialized.features.filter(f => isPrimaryType(f.type));

  // Handle edge case: no primary features (simple molecules like HCl, H2O)
  if (primaries.length === 0) {
    // PIPH-233: append " radical" to the molecule label when any atom carries
    // a radical (e.g. "Br" → "Br radical"). Mirrors describeSingleMolecule.
    const radicalSuffix = serialized.atoms.some(a => (a.radicals ?? 0) > 0) ? ' radical' : '';

    // Check for molecule-type features first (simple molecules)
    const moleculeFeature = serialized.features.find(f => f.type === 'molecule');
    if (moleculeFeature) {
      return moleculeFeature.name + radicalSuffix;
    }

    // Fall back to listing all features
    if (serialized.features.length === 0) {
      return 'Empty structure';
    }

    // Check for atom-type features (very simple molecules like AlCl₃, BF₃)
    const atomFeatures = serialized.features.filter(f => f.type === 'atom');
    if (atomFeatures.length > 0 && serialized.features.length === atomFeatures.length) {
      // PIPH-140: Use allAtomSymbols if available (includes implicit H),
      // otherwise fall back to serialized.atoms (may miss implicit H)
      const atomSymbols = allAtomSymbols ?? serialized.atoms.map(a => a.symbol);
      const formula = assembleMolecularFormula(atomSymbols);
      if (formula) {
        // PIPH-140: Append charge suffix if present
        return formula + formatChargeSuffix(netCharge) + radicalSuffix;
      }
      // Fallback to atom names if formula assembly fails
      return atomFeatures.map(f => f.name).join(' ') + radicalSuffix;
    }

    // Just list the functional groups without a core
    const fgLines = serialized.features
      .filter(f => f.type === 'functional_group')
      .map(f => `- ${f.name}`);
    return fgLines.length > 0
      ? fgLines.join('\n')
      : 'Structure with no identifiable features';
  }

  // Step 3: Determine core feature
  const core = determineCore(primaries, serialized);

  // Step 4: Group functional groups by parent
  const functionalGroupsByParent = groupFunctionalGroupsByParent(serialized);

  // Step 4.5 (PIPH-100): Compute bidirectional connections between primaries
  const connectionMap = computePrimaryConnections(serialized, primaries);

  // Step 4.6 (PIPH-91): Identify and classify unclaimed atoms
  const atomFeatures = serialized.features.filter(f => f.type === 'atom');
  const unclaimedAtoms = classifyUnclaimedAtoms(atomFeatures, primaries, serialized);
  const attachedAtomsByParent = groupUnclaimedAtomsByParent(unclaimedAtoms);

  // Get bridging atoms (attached to two primaries)
  const bridgingAtoms = unclaimedAtoms.filter(a => a.bridging !== null);

  // PIPH-231: Unrecognized atoms get their own block-level treatment, whether
  // they're truly standalone (counterion, free ion) or attached via a
  // functional group (e.g. TBDMS bonded to the oxygen of an ether). Both cases
  // appear in the same trailing section.
  const unrecognizedAtoms = unclaimedAtoms.filter(a => a.standalone || a.attachedViaFg);

  // Step 5: Build feature blocks for each primary
  const blocks: FeatureBlock[] = primaries.map(primary => {
    const functionalGroups = functionalGroupsByParent.get(primary.id) || [];
    const attachedAtoms = attachedAtomsByParent.get(primary.id) || [];
    // Get bridging atoms that touch this primary (for connection section)
    const relevantBridgingAtoms = bridgingAtoms.filter(
      a => a.bridging && (a.bridging[0].id === primary.id || a.bridging[1].id === primary.id)
    );
    return buildFeatureBlock(
      primary,
      functionalGroups,
      attachedAtoms,
      relevantBridgingAtoms,
      serialized,
      primaries,
      connectionMap,
      resolve,
    );
  });

  // PIPH-149: Deduplicate blocks with identical feature names
  // This handles cases where the serialized features contain duplicate entries
  const seenNames = new Set<string>();
  const deduplicatedBlocks: FeatureBlock[] = [];
  for (const block of blocks) {
    const name = block.displayName;
    if (!seenNames.has(name)) {
      seenNames.add(name);
      deduplicatedBlocks.push(block);
    }
  }

  // Step 6: Format and return
  return formatOutput(core, deduplicatedBlocks, unrecognizedAtoms, serialized, resolve);
}
